import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { BrandedLoading, BrandedSpinner } from '@/components/common/BrandedLoading';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { onboardingService, DEFAULT_ONBOARDING_SURVEY_ID } from '@/services/onboardingService';
import { profileApi } from '@/services/profileApi';
import { learningPlanApi } from '@/services/learningManagementApi';
import { CopyableId } from '@/components/common';
import { QuestionCard } from '@/features/onboarding/components/QuestionCard';
import { ResponseQuoteCard } from '@/features/onboarding/components/ResponseQuoteCard';
import { BannerCard } from '@/features/onboarding/components/BannerCard';
import { ProcessingOverlay } from '@/components/onboarding/ProcessingOverlay';
import OnboardingCompletionPage from '@/pages/OnboardingCompletionPage';
import AuthPage from '@/pages/AuthPage';
import SubscriptionPlanSelectionPage from '@/pages/SubscriptionPlanSelectionPage';
import PaymentCheckoutPage from '@/pages/billing/PaymentCheckoutPage';
import OnboardingConsentPage from '@/pages/OnboardingConsentPage';
import { getBrowserDeviceInfo } from '@/utils/device';
import { isLocalMode } from '@/utils/localMode';
import { fetchIdentityProfile } from '@/services/authApi';
import { sampleOnboardingSurvey } from '@/data/sampleOnboardingSurvey';
import type {
  DeviceInfo,
  OnboardingSession,
  OnboardingStartResponse,
  ResponseQuote,
  StartOnboardingPayload,
  Survey,
  SurveyQuestion,
  SurveyResponseRecord,
  UserOnboardingRequest,
} from '@/types';
import type { UserProfile, Bio } from '@/types/userProfile';
import type { LearningPlan } from '@/types/learningManagement';

const SESSION_STORAGE_KEY = 'five_ai_onboarding_session_id';
const SESSION_EMAIL_KEY = 'five_ai_onboarding_session_email'; // email that started this session (from home); used to force new session when user returns with different email
const ANSWERS_STORAGE_KEY = 'five_ai_onboarding_answers';
const QUOTES_STORAGE_KEY = 'five_ai_onboarding_quotes';
const STEP_STORAGE_KEY = 'five_ai_onboarding_current_index';
const COMPLETE_REQUEST_STORAGE_KEY = 'five_ai_onboarding_complete_request';
const AUTH_PROVIDER_STORAGE_KEY = 'five_ai_onboarding_auth_provider';
/** Explicit UI flag: new user just completed registration on auth server → land on subscription. Set by AuthPage (before OAuth) and OAuthCallback (after OAuth). */
const RETURNED_FROM_REGISTRATION_KEY = 'five_ai_onboarding_returned_from_registration';
const PLAN_CODE_STORAGE_KEY = 'five_ai_onboarding_plan_code';
const SELECTED_PLAN_DATA_KEY = 'five_ai_onboarding_selected_plan_data';
const LOCAL_USER_INFO_KEY = 'five_ai_local_user_info';

const answerTypeMap: Record<string, string> = {
  TEXT_SHORT: 'TEXT',
  TEXT_LONG: 'TEXT',
  SINGLE_CHOICE: 'SINGLE_CHOICE',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  NUMBER: 'NUMBER',
  EMAIL: 'EMAIL',
  DATE: 'DATE',
  RATING_SCALE: 'RATING_SCALE',
  BOOLEAN: 'BOOLEAN',
  DROPDOWN: 'SINGLE_CHOICE',
  SLIDER: 'NUMBER',
  HEADING: 'TEXT', // HEADING doesn't need answer but map it for completeness
  BANNER: 'TEXT', // BANNER doesn't need answer but map it for completeness
};

const storeJSON = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(value));
};

const storeSessionId = (sessionId: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
};

const getStoredSessionId = () => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(SESSION_STORAGE_KEY);
};

/** Intent email from home (five_ai_local_user_info). Used to force new session when it differs from session email. */
const getIntentEmail = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LOCAL_USER_INFO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { signUpEmail?: string; email?: string };
    const email = (parsed.signUpEmail ?? parsed.email ?? '').trim().toLowerCase();
    return email || null;
  } catch {
    return null;
  }
};

/** Persist intent email (same as Hero) so getIntentEmail() returns it and session can use it. */
const persistIntentEmail = (email: string) => {
  if (typeof window === 'undefined') return;
  try {
    const trimmed = email.trim().toLowerCase();
    window.localStorage.setItem(
      LOCAL_USER_INFO_KEY,
      JSON.stringify({ email: trimmed, signUpEmail: trimmed })
    );
  } catch {
    // ignore
  }
};

const getStoredSessionEmail = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const email = (window.localStorage.getItem(SESSION_EMAIL_KEY) ?? '').trim().toLowerCase();
    return email || null;
  } catch {
    return null;
  }
};

const setStoredSessionEmail = (email: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (email == null || email === '') window.localStorage.removeItem(SESSION_EMAIL_KEY);
    else window.localStorage.setItem(SESSION_EMAIL_KEY, email.trim().toLowerCase());
  } catch {
    // ignore
  }
};

// Survey / plan UI cache only. Do not remove OAuth return flags here — those are read on the next
// createOrResumeSession and cleared only after we advance (or on explicit Reset); wiping them during
// session resume/new-session setup was breaking post-survey auth and subscription auto-advance.
const clearOnboardingSurveyUiCache = () => {
  if (typeof window === 'undefined') return;
  const keys = [
    ANSWERS_STORAGE_KEY,
    QUOTES_STORAGE_KEY,
    STEP_STORAGE_KEY,
    COMPLETE_REQUEST_STORAGE_KEY,
    PLAN_CODE_STORAGE_KEY,
    SELECTED_PLAN_DATA_KEY,
  ];
  for (const key of keys) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }
};

const clearOnboardingAuthFlowKeys = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
    window.localStorage.removeItem(RETURNED_FROM_REGISTRATION_KEY);
  } catch {
    // ignore
  }
};

/** Full local onboarding UI + OAuth-return flags (e.g. Reset button). */
const clearOnboardingPageStateCache = () => {
  clearOnboardingSurveyUiCache();
  clearOnboardingAuthFlowKeys();
};

/** True when the session is completed; such sessions must not be resumed so the user can start fresh (e.g. different email).
 * We only use completedAt or status=COMPLETED. We do NOT use userId: the backend may set userId when the user
 * registers (OAuth) while onboarding is still in progress; treating that as "completed" caused a second
 * startOnboarding(forceNew=true) and abandoned the in-progress session with survey responses. */
const isSessionCompleted = (s: { completedAt?: unknown; status?: string } | null | undefined): boolean =>
  !!(s?.completedAt || (typeof s?.status === 'string' && s.status.toUpperCase() === 'COMPLETED'));

/** Max time to wait for profile + learning path to be built before showing Welcome page anyway. */
const BUILDING_TIMEOUT_MS = 3 * 60 * 1000;

const BUILDING_MESSAGES = [
  'Hang tight — setting up your profile...',
  'Generating your AI bio and career story...',
  'Building your personalized learning path...',
  'Optimizing your plan for your goals...',
  'Final checks before your Welcome page...',
];

const OnboardingPage = () => {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [session, setSession] = useState<OnboardingSession | null>(null);
  // IMPORTANT:
  // We treat the backend session as the source of truth. Local storage is used only as a cache,
  // and will be re-hydrated from the server via /onboarding/device/:deviceId (or session details).
  // This avoids stale local caches showing "100%" when the backend has no active session.
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(true);
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeQuote, setActiveQuote] = useState<ResponseQuote | null>(null);
  const [activeBanner, setActiveBanner] = useState<SurveyQuestion | null>(null);
  const [quoteHistory, setQuoteHistory] = useState<Set<string>>(
    () => new Set()
  );
  const [submitting, setSubmitting] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false); // Survey questions completed
  const [showAuth, setShowAuth] = useState(false); // Show auth page
  const [showSubscriptionPlans, setShowSubscriptionPlans] = useState(false); // Show subscription plan selection
  const [showCheckout, setShowCheckout] = useState(false); // Show payment checkout page
  const [showConsent, setShowConsent] = useState(false); // Show terms/consent page
  const [profileStepEmail, setProfileStepEmail] = useState<string | null>(null); // After consent: show building view, poll until profile+bio and path ready, then show Welcome page
  const buildingStartTimeRef = useRef<number>(0); // When we started polling (for 3 min timeout)
  const buildingEmailRef = useRef<string | null>(null); // Email used in building phase, so completion page can show it when completionData is empty (e.g. timeout)
  const [buildProfileReady, setBuildProfileReady] = useState(false);
  const [buildBioReady, setBuildBioReady] = useState(false);
  const [buildPathReady, setBuildPathReady] = useState(false);
  const [buildMessageIndex, setBuildMessageIndex] = useState(0);
  const [buildLogs, setBuildLogs] = useState<string[]>([]);
  const buildStepTransitionRef = useRef({ profile: false, bio: false, path: false });
  const [intentEmailFromGate, setIntentEmailFromGate] = useState<string | null>(null); // Email submitted on gate when no intent email (new user)
  const [emailGateValue, setEmailGateValue] = useState('');
  const [emailGateChecking, _setEmailGateChecking] = useState(false); // UI only; gate no longer does async check (start API handles redirect)
  const [emailGateError, setEmailGateError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false); // Processing after complete
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false); // Final completion - show profile page
  const [completionData, setCompletionData] = useState<{
    userProfile?: Record<string, unknown>;
    bio?: Record<string, unknown>;
    learningPath?: Record<string, unknown>;
  } | null>(null);
  const lastLocationUpdateSessionIdRef = useRef<string | null>(null);
  const didAutoAdvanceRef = useRef(false);
  // Capture "returned from registration" flag on mount BEFORE session loading can clear it.
  // Use AUTH_PROVIDER or RETURNED_FROM_REGISTRATION_KEY so we land on subscription after OAuth.
  const returnedFromAuthRef = useRef<string | null>(
    typeof window !== 'undefined' && (localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY) || localStorage.getItem(RETURNED_FROM_REGISTRATION_KEY))
      ? '1'
      : null
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated: isAuthContextAuthenticated, isLoading: isAuthLoading, user: authUser } = useAuth();

  // Email from URL (e.g. home page → /onboarding?email=...) so we skip the gate and prepopulate
  const emailFromUrl = useMemo(() => {
    const raw = searchParams.get('email');
    if (!raw?.trim()) return null;
    return raw.trim().toLowerCase();
  }, [searchParams]);

  // Returned from login to show subscription (home/Get started → REDIRECT_LOGIN → login → back to onboarding)
  const showSubscriptionParam = searchParams.get('subscription') === '1';

  useEffect(() => {
    if (!emailFromUrl) return;
    persistIntentEmail(emailFromUrl);
    searchParams.delete('email');
    const next = searchParams.toString();
    setSearchParams(next ? `?${next}` : '', { replace: true });
  }, [emailFromUrl]); // eslint-disable-line react-hooks/exhaustive-deps -- persist once when email from URL is present; avoid setSearchParams in deps to prevent loop

  // Intent email (from home URL, gate submit, or localStorage). Defined early so session effect can depend on it.
  const hasIntentEmail = useMemo(
    () => !!emailFromUrl || !!getIntentEmail() || !!intentEmailFromGate,
    [emailFromUrl, intentEmailFromGate]
  );

  const didCheckExistingUserRef = useRef(false);
  /** Set when start API returns REDIRECT_LOGIN so we don't set error in executeSession. */
  const redirectToLoginRef = useRef(false);

  // "User already onboarded?" is now handled by POST /onboarding/start response (action === 'REDIRECT_LOGIN'). No separate getByEmail call.

  // Gate: when logged in and profile exists → send to coach (skip onboarding).
  // Skip when subscription=1: user is returning from login to see subscription page (home/Get started flow).
  // Unauthenticated users at start are allowed to see the survey first; auth is shown after survey completion.
  useEffect(() => {
    if (isAuthLoading) return;
    if (!isAuthContextAuthenticated) return; // Let unauthenticated users proceed to survey (no redirect to login at start)
    if (showSubscriptionParam) return; // Returning from login to subscription — don't redirect to coach

    if (didCheckExistingUserRef.current) return;
    didCheckExistingUserRef.current = true;

    // Resolve email: use authUser.email if available, otherwise fetch from identity profile
    const resolveEmail = async (): Promise<string> => {
      if (authUser?.email) return authUser.email;
      const token = localStorage.getItem('five_ai_access_token');
      if (!token) return '';
      try {
        const profile = await fetchIdentityProfile(token);
        return profile.email ?? '';
      } catch {
        return '';
      }
    };

    resolveEmail()
      .then((email) => {
        if (!email) return; // No email — stay in onboarding (new user)
        return profileApi.getByEmail(email).then(() => {
          navigate('/coach', { replace: true });
        });
      })
      .catch(() => {
        // Email not registered (no profile) — keep user in onboarding flow
      });
  }, [isAuthLoading, isAuthContextAuthenticated, authUser?.email, navigate, showAuth, showSubscriptionPlans, showConsent, isCompleted, profileStepEmail, showSubscriptionParam]);

  // When registration happens in the onboarding flow: user returns from auth server (authenticated + flag set).
  // Skip survey/auth and go straight to subscription. Track via AUTH_PROVIDER or RETURNED_FROM_REGISTRATION_KEY.
  useEffect(() => {
    if (didAutoAdvanceRef.current) return;
    if (!isAuthContextAuthenticated) return;
    if (showSubscriptionPlans || showCheckout || showConsent || isCompleted) return; // already past this point

    const hasReturnedFromRegistration =
      returnedFromAuthRef.current ||
      (typeof window !== 'undefined' && !!window.localStorage.getItem(RETURNED_FROM_REGISTRATION_KEY));
    if (!hasReturnedFromRegistration) return;

    const sessionId = session?.sessionId || getStoredSessionId();
    if (!sessionId) return; // no session yet — session may load later and re-run this effect

    didAutoAdvanceRef.current = true;
    returnedFromAuthRef.current = null;
    try {
      localStorage.removeItem(AUTH_PROVIDER_STORAGE_KEY);
      localStorage.removeItem(RETURNED_FROM_REGISTRATION_KEY);
    } catch {
      /* ignore */
    }
    setSurveyCompleted(true);
    setShowAuth(false);
    setShowSubscriptionPlans(true);
    // If session state wasn't set yet (e.g. async load still pending), set minimal session so subscription page can render
    if (!session?.sessionId) {
      setSession({ sessionId } as OnboardingSession);
    }
  }, [isAuthContextAuthenticated, session?.sessionId, showSubscriptionPlans, showCheckout, showConsent, isCompleted]);

  // Sign in → new user registers → onboarding (same as Get started): they complete survey already authenticated. Skip auth step, go to subscription.
  useEffect(() => {
    if (!showAuth || !surveyCompleted || !isAuthContextAuthenticated) return;
    if (showSubscriptionPlans || showCheckout || showConsent || isCompleted) return;
    setShowAuth(false);
    setShowSubscriptionPlans(true);
  }, [showAuth, surveyCompleted, isAuthContextAuthenticated, showSubscriptionPlans, showCheckout, showConsent, isCompleted]);

  // After consent: poll for profile (with bio) and learning path; show building view until both ready (or 3 min timeout), then show Welcome page with data
  useEffect(() => {
    const email = profileStepEmail?.trim();
    if (!email) return;

    buildingEmailRef.current = email;
    buildingStartTimeRef.current = Date.now();
    let cancelled = false;

    // Reset building UI (progress + log) for each building run.
    setBuildProfileReady(false);
    setBuildBioReady(false);
    setBuildPathReady(false);
    setBuildMessageIndex(0);
    setBuildLogs(['Starting your setup...']);
    buildStepTransitionRef.current = { profile: false, bio: false, path: false };

    const hasPathContent = (p: LearningPlan | null | undefined): boolean => {
      if (!p) return false;
      const ext = p as unknown as Record<string, unknown>;
      const mods = p.learningModules ?? ext.learningPathDetails;
      const arr = Array.isArray(mods) ? mods : [];
      if (arr.length > 0) return true;
      return Array.isArray(ext.goals) && (ext.goals as unknown[]).length > 0;
    };

    const poll = async () => {
      if (cancelled) return true;
      const timedOut = Date.now() - buildingStartTimeRef.current >= BUILDING_TIMEOUT_MS;
      if (timedOut) {
        try {
          const [profileRes, pathRes] = await Promise.all([
            profileApi.getByEmail(email).catch(() => null),
            learningPlanApi.getByUser(email).catch(() => null),
          ]);
          if (!cancelled) {
            const profileReady = !!profileRes?.userId;
            const bioReady = !!profileRes?.bio;
            const pathReady = hasPathContent(pathRes ?? null);

            setBuildProfileReady(profileReady);
            setBuildBioReady(bioReady);
            setBuildPathReady(pathReady);

            if (profileReady && !buildStepTransitionRef.current.profile) {
              buildStepTransitionRef.current.profile = true;
              setBuildLogs((prev) => [...prev, '✓ Profile created.']);
            }
            if (bioReady && !buildStepTransitionRef.current.bio) {
              buildStepTransitionRef.current.bio = true;
              setBuildLogs((prev) => [...prev, '✓ AI bio generated.']);
            }
            if (pathReady && !buildStepTransitionRef.current.path) {
              buildStepTransitionRef.current.path = true;
              setBuildLogs((prev) => [...prev, '✓ Learning path ready.']);
            }

            setCompletionData({
              userProfile: profileRes as unknown as Record<string, unknown> ?? undefined,
              bio: (profileRes?.bio ?? profileRes) as unknown as Record<string, unknown> ?? undefined,
              learningPath: pathRes as unknown as Record<string, unknown> ?? undefined,
            });
          }
        } catch {
          setCompletionData({});
        }
        if (!cancelled) {
          setIsCompleted(true);
          setProfileStepEmail(null);
        }
        return true;
      }
      try {
        const [profileRes, pathRes] = await Promise.all([
          profileApi.getByEmail(email).catch(() => null),
          learningPlanApi.getByUser(email).catch(() => null),
        ]);
        if (cancelled) return true;

        const profileReady = !!profileRes?.userId;
        const bioReady = !!profileRes?.bio;
        const pathReady = hasPathContent(pathRes ?? null);

        setBuildProfileReady(profileReady);
        setBuildBioReady(bioReady);
        setBuildPathReady(pathReady);

        if (profileReady && !buildStepTransitionRef.current.profile) {
          buildStepTransitionRef.current.profile = true;
          setBuildLogs((prev) => [...prev, '✓ Profile created.']);
        }
        if (bioReady && !buildStepTransitionRef.current.bio) {
          buildStepTransitionRef.current.bio = true;
          setBuildLogs((prev) => [...prev, '✓ AI bio generated.']);
        }
        if (pathReady && !buildStepTransitionRef.current.path) {
          buildStepTransitionRef.current.path = true;
          setBuildLogs((prev) => [...prev, '✓ Learning path ready.']);
        }

        const hasProfileWithBio = profileReady && bioReady;
        if (hasProfileWithBio && pathReady && profileRes && pathRes) {
          setCompletionData({
            userProfile: profileRes as unknown as Record<string, unknown>,
            bio: (profileRes.bio ?? profileRes) as unknown as Record<string, unknown>,
            learningPath: pathRes as unknown as Record<string, unknown>,
          });
          // Profile + learning path built → show checkout before final completion
          // User now exists in the system so subscription/payment APIs will work
          setProfileStepEmail(null);
          const hasPlanSelected = !!localStorage.getItem(PLAN_CODE_STORAGE_KEY);
          if (hasPlanSelected) {
            setShowCheckout(true);
          } else {
            setIsCompleted(true);
          }
          return true;
        }
        if (Date.now() - buildingStartTimeRef.current >= BUILDING_TIMEOUT_MS) {
          // Final pass: update progress UI even if we are timing out.
          if (profileReady && !buildStepTransitionRef.current.profile) {
            buildStepTransitionRef.current.profile = true;
            setBuildLogs((prev) => [...prev, '✓ Profile created.']);
          }
          if (bioReady && !buildStepTransitionRef.current.bio) {
            buildStepTransitionRef.current.bio = true;
            setBuildLogs((prev) => [...prev, '✓ AI bio generated.']);
          }
          if (pathReady && !buildStepTransitionRef.current.path) {
            buildStepTransitionRef.current.path = true;
            setBuildLogs((prev) => [...prev, '✓ Learning path ready.']);
          }

          setCompletionData({
            userProfile: profileRes as unknown as Record<string, unknown> ?? undefined,
            bio: profileRes?.bio as unknown as Record<string, unknown> ?? undefined,
            learningPath: pathRes as unknown as Record<string, unknown> ?? undefined,
          });
          // Timeout: show checkout if plan was selected, otherwise complete directly
          setProfileStepEmail(null);
          const hasPlanSelected = !!localStorage.getItem(PLAN_CODE_STORAGE_KEY);
          if (hasPlanSelected) {
            setShowCheckout(true);
          } else {
            setIsCompleted(true);
          }
          return true;
        }
      } catch {
        // ignore, will retry
      }
      return false;
    };

    const interval = setInterval(async () => {
      const done = await poll();
      if (done) clearInterval(interval);
    }, 3000);
    poll(); // first run

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [profileStepEmail]);

  // Rotate fancy status messages while we are in the building phase.
  useEffect(() => {
    if (!profileStepEmail) return;
    const t = window.setInterval(() => {
      setBuildMessageIndex((prev) => (prev + 1) % BUILDING_MESSAGES.length);
    }, 2400);
    return () => window.clearInterval(t);
  }, [profileStepEmail]);

  const buildPercent =
    (buildProfileReady ? 33 : 0) + (buildBioReady ? 33 : 0) + (buildPathReady ? 34 : 0);

  // Filter questions based on conditional rules and compute visible questions
  const visibleQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    
    return survey.questions.filter((question) => {
      // If no conditional rules, always show
      if (!question.conditionalRules || question.conditionalRules.length === 0) {
        return true;
      }
      
      // Evaluate all conditional rules
      for (const rule of question.conditionalRules) {
        try {
          // Evaluate condition string (e.g., "pain_points.includes('pain_confidence')")
          // Create a safe evaluation context with answers
          const evalCondition = (condition: string, answersData: Record<string, string | string[]>): boolean => {
            // Simple condition evaluation - supports .includes() for arrays
            // Example: "pain_points.includes('pain_confidence')"
            
            // Check for .includes() pattern
            const includesMatch = condition.match(/(\w+)\.includes\(['"]([^'"]+)['"]\)/);
            if (includesMatch) {
              const [, key, value] = includesMatch;
              const answerValue = answersData[key];
              if (Array.isArray(answerValue)) {
                return answerValue.includes(value);
              }
              return false;
            }
            
            // Check for equality patterns: key === 'value' or key == 'value'
            const equalityMatch = condition.match(/(\w+)\s*(===|==)\s*['"]([^'"]+)['"]/);
            if (equalityMatch) {
              const [, key, , value] = equalityMatch;
              const answerValue = answersData[key];
              return String(answerValue) === value;
            }
            
            // If pattern not recognized, default to false (don't show)
            return false;
          };
          
          const shouldShow = evalCondition(rule.condition, answers);
          
          if (rule.action === 'HIDE' && shouldShow) {
            return false;
          }
          if (rule.action === 'SHOW' && !shouldShow) {
            return false;
          }
        } catch {
          // If evaluation fails, show the question (fail open)
          return true;
        }
      }
      
      return true;
    }).sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [survey?.questions, answers]);

  // Adjust currentIndex if it's out of bounds after filtering
  useEffect(() => {
    if (visibleQuestions.length > 0 && currentIndex >= visibleQuestions.length) {
      setCurrentIndex(Math.max(0, visibleQuestions.length - 1));
    }
  }, [visibleQuestions.length, currentIndex]);

  // When current question is BANNER, show the banner overlay immediately (message-only, no input)
  useEffect(() => {
    const q = visibleQuestions[currentIndex];
    if (q?.type === 'BANNER' && !activeBanner) {
      setActiveBanner(q);
    }
  }, [currentIndex, visibleQuestions, activeBanner]);

  const currentQuestion =
    !isCompleted && visibleQuestions.length > 0 ? visibleQuestions[currentIndex] : null;

  const progressValue = useMemo(() => {
    if (isCompleted) {
      return 100;
    }
    if (session?.completionPercentage) {
      return Math.min(100, session.completionPercentage);
    }
    if (!visibleQuestions.length) return 0;
    return Math.round(((currentIndex + 1) / visibleQuestions.length) * 100);
  }, [currentIndex, isCompleted, visibleQuestions.length, session?.completionPercentage]);

  useEffect(() => {
    storeJSON(ANSWERS_STORAGE_KEY, answers);
  }, [answers]);

  useEffect(() => {
    storeJSON(STEP_STORAGE_KEY, currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    storeJSON(QUOTES_STORAGE_KEY, Array.from(quoteHistory));
  }, [quoteHistory]);

  const loadSurvey = useCallback(async (abortController?: AbortController) => {
    const controller = abortController || new AbortController();
    
    setIsLoadingSurvey(true);
    setError(null);
    try {
      const data = await onboardingService.fetchSurvey(
        DEFAULT_ONBOARDING_SURVEY_ID
      );
      // Check if component is still mounted before updating state
      if (!controller.signal.aborted) {
        setSurvey(data);
      }
    } catch (err) {
      // Only update state if not aborted
      if (!controller.signal.aborted) {
        console.error('Failed to load survey, using fallback data', err);
        setSurvey(sampleOnboardingSurvey);
        setError(
          'Unable to reach the onboarding survey API. Showing cached survey so you can continue iterating. Refresh once the service is available.'
        );
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoadingSurvey(false);
      }
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    loadSurvey(abortController);
    
    return () => {
      abortController.abort();
    };
  }, [loadSurvey]);

  const hydrateResponses = (
    sessionData: OnboardingSession | (OnboardingSession & { surveyResponses?: Array<SurveyResponseRecord & { selectedOptions?: string[] }> }),
    surveyData: Survey | null,
    options?: { replace?: boolean }
  ) => {
    if (!sessionData.surveyResponses?.length) {
      console.log('[Onboarding] No survey responses to hydrate from session data');
      return;
    }

    console.log('[Onboarding] Hydrating responses from session:', {
      sessionId: sessionData.sessionId,
      responseCount: sessionData.surveyResponses.length
    });

    const questionById = new Map(
      (surveyData?.questions ?? []).map((q) => [q.id, q])
    );

    const toValues = (questionId: string, raw: string[]): string[] => {
      const q = questionById.get(questionId);
      if (!q?.options?.length) return raw;
      return raw.map((sel) => {
        const s = String(sel).trim();
        const opt = q.options!.find(
          (o) =>
            o.id === s ||
            o.id === sel ||
            (o.value != null && String(o.value).trim() === s) ||
            (o.text != null && String(o.text).trim() === s)
        );
        const v = opt?.value != null && String(opt.value).trim() !== '' ? String(opt.value).trim() : '';
        return v.length > 0 ? v : opt?.id ?? sel;
      });
    };

    const responseMap = sessionData.surveyResponses.reduce<
      Record<string, string | string[]>
    >((acc, response) => {
      // Handle different answer types from the API
      const responseWithOptions = response as SurveyResponseRecord & { selectedOptions?: string[] };
      
      if (response.answerType === 'MULTIPLE_CHOICE') {
        let raw: string[] = [];
        if (responseWithOptions.selectedOptions && Array.isArray(responseWithOptions.selectedOptions)) {
          raw = responseWithOptions.selectedOptions;
        } else if (response.answer && typeof response.answer === 'string') {
          raw = response.answer.split(',').map((s) => s.trim()).filter(Boolean);
        }
        if (raw.length) {
          acc[response.questionId] = toValues(response.questionId, raw);
        }
      } else if (response.answerType === 'SINGLE_CHOICE') {
        const selection =
          responseWithOptions.selectedOptions?.[0] ||
          (typeof response.answer === 'string' ? response.answer : '');
        if (selection) {
          const q = questionById.get(response.questionId);
          const s = String(selection).trim();
          const opt = q?.options?.find(
            (o) =>
              o.id === s || o.id === selection ||
              (o.value != null && String(o.value).trim() === s) ||
              (o.text != null && String(o.text).trim() === s)
          );
          const v = opt?.value != null && String(opt.value).trim() !== '' ? String(opt.value).trim() : '';
          acc[response.questionId] = v.length > 0 ? v : opt?.id ?? selection;
        }
      } else if (response.answer) {
        acc[response.questionId] =
          typeof response.answer === 'string' ? response.answer : String(response.answer);
      }
      return acc;
    }, {});

    console.log('[Onboarding] Hydrated response map:', Object.keys(responseMap));
    if (options?.replace) {
      setAnswers(responseMap);
      return;
    }
    setAnswers((prev) => ({ ...prev, ...responseMap }));
  };

  // Location update is optional and non-breaking. If we can retrieve full info (timezone + coords), send it; else send timezone only. All errors are swallowed.
  const updateLocationBestEffort = useCallback(
    async (sessionId: string, deviceInfo: DeviceInfo) => {
      const timezone =
        deviceInfo.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC';

      const tz = typeof timezone === 'string' && timezone.length > 0 ? timezone : 'UTC';

      // Payload: only allowed fields (latitude, longitude, timezone). Send full info when we have it, else timezone only.
      const buildPayload = (opts: { latitude?: number; longitude?: number }): Record<string, string | number> => {
        const payload: Record<string, string | number> = { timezone: tz };
        if (
          typeof opts.latitude === 'number' &&
          Number.isFinite(opts.latitude) &&
          opts.latitude >= -90 &&
          opts.latitude <= 90 &&
          typeof opts.longitude === 'number' &&
          Number.isFinite(opts.longitude) &&
          opts.longitude >= -180 &&
          opts.longitude <= 180
        ) {
          payload.latitude = opts.latitude;
          payload.longitude = opts.longitude;
        }
        return payload;
      };

      const sendOptional = async (payload: Record<string, string | number>) => {
        try {
          await onboardingService.updateLocation(sessionId, payload);
          console.log('[Onboarding] Location updated (optional)');
        } catch {
          // Optional API: do not break flow or surface errors; do not retry (service uses skipRetry).
        }
      };

      if (typeof navigator === 'undefined' || !navigator.geolocation) {
        await sendOptional(buildPayload({}));
        return;
      }

      let permissionDenied = false;
      if (navigator.permissions?.query) {
        try {
          const status = await navigator.permissions.query({ name: 'geolocation' });
          if (status.state === 'denied') permissionDenied = true;
        } catch {
          // continue
        }
      }

      if (permissionDenied) {
        await sendOptional(buildPayload({}));
        return;
      }

      const coords = await new Promise<{ latitude: number; longitude: number } | null>((resolve) => {
        try {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lon = position.coords.longitude;
              resolve(Number.isFinite(lat) && Number.isFinite(lon) ? { latitude: lat, longitude: lon } : null);
            },
            () => resolve(null),
            { enableHighAccuracy: false, timeout: 10000, maximumAge: 300_000 }
          );
        } catch {
          resolve(null);
        }
      });

      if (coords) {
        await sendOptional(buildPayload({ latitude: coords.latitude, longitude: coords.longitude }));
      } else {
        await sendOptional(buildPayload({}));
      }
    },
    []
  );

  // Ensure location is attempted at least once per sessionId (covers resume-by-device and resume-by-session flows).
  useEffect(() => {
    const sessionId = session?.sessionId;
    if (!sessionId) return;
    if (lastLocationUpdateSessionIdRef.current === sessionId) return;
    lastLocationUpdateSessionIdRef.current = sessionId;
    updateLocationBestEffort(sessionId, getBrowserDeviceInfo());
  }, [session?.sessionId, updateLocationBestEffort]);

  const createOrResumeSession = useCallback(async (options?: { forceNew?: boolean }) => {
    if (!survey) return null;

    const abortController = new AbortController();
    setIsSessionLoading(true);
    setError(null);
    try {
      const storedSessionId = getStoredSessionId();
      const deviceInfo = getBrowserDeviceInfo();
      const forceNew = options?.forceNew === true;
      let forceCreateNew = false;

      const clampIndex = (index: number) => {
        if (!Number.isFinite(index)) return 0;
        const max = Math.max(0, visibleQuestions.length - 1);
        return Math.min(Math.max(0, Math.floor(index)), max);
      };

      const resetStateForNewSession = () => {
        clearOnboardingPageStateCache(); // avoid stale answers/quotes/step and other UI cache across sessions
        setAnswers({});
        setCurrentIndex(0);
        setQuoteHistory(new Set());
        setSession(null);
      };

      const intentEmail = getIntentEmail();

      // Resume/create order: 1) intent email → force new (so backend can REDIRECT_LOGIN). 2) Returning from OAuth → resume by stored id. 3) No intent → try device, then stored id, then create. Send forceNew only when we explicitly want to abandon (email change, completed session, or caller passed forceNew).
      // When user came from home or gate with an email, we must call POST /onboarding/start first so the backend
      // can return REDIRECT_LOGIN for existing users. Skip device/stored resume entirely.
      // Exception 1: if user is returning from OAuth registration (AUTH_PROVIDER_STORAGE_KEY set), do NOT clear
      // cache — we need to preserve session id and auth flag so the "returned from auth" effect can advance to
      // subscription page instead of restarting onboarding.
      // Exception 2: if user is returning from login to subscription (subscription=1 + authenticated), do NOT clear
      // cache — we'll call start with auth so backend can return a session for the subscription step.
      const returningFromOAuth =
        typeof window !== 'undefined' &&
        !!(window.localStorage.getItem(AUTH_PROVIDER_STORAGE_KEY) || window.localStorage.getItem(RETURNED_FROM_REGISTRATION_KEY));
      const returningToSubscription = showSubscriptionParam && isAuthContextAuthenticated;
      if (intentEmail && !forceNew && !returningFromOAuth && !returningToSubscription) {
        console.log('[Onboarding] Intent email present — skipping device resume, calling start API so backend can return REDIRECT_LOGIN if user exists.');
        // Only clear survey/plan UI cache — not OAuth return keys (those are unset or unused here, but a full
        // clear was wiping flags still needed when the user immediately returns from auth/subscription flows).
        clearOnboardingSurveyUiCache();
        setAnswers({});
        setCurrentIndex(0);
        setQuoteHistory(new Set());
        setSession(null);
        forceCreateNew = true;
      }

      // Returning from OAuth registration (new user): resume existing session so we go to subscription, not restart survey.
      if (returningFromOAuth && storedSessionId && !forceNew) {
        try {
          const sessionStatus = await onboardingService.getSession(storedSessionId);
          if (!abortController.signal.aborted && sessionStatus?.sessionId) {
            try {
              const sessionDetails = await onboardingService.getSessionDetails(storedSessionId);
              if (!abortController.signal.aborted && sessionDetails && !isSessionCompleted(sessionDetails)) {
                console.log('[Onboarding] Resuming session after OAuth registration:', storedSessionId);
                setSession(sessionDetails);
                storeSessionId(sessionDetails.sessionId);
                hydrateResponses(sessionDetails, survey, { replace: true });
                if (typeof sessionDetails.currentStep === 'number') {
                  setCurrentIndex(clampIndex(sessionDetails.currentStep));
                }
                return sessionDetails;
              }
            } catch {
              /* ignore details failure */
            }
            if (!abortController.signal.aborted && sessionStatus) {
              setSession(sessionStatus);
              if (typeof sessionStatus.currentStep === 'number') {
                setCurrentIndex(clampIndex(sessionStatus.currentStep));
              }
              return sessionStatus;
            }
          }
        } catch {
          console.warn('[Onboarding] Failed to resume session after OAuth, will create new session');
        }
      }

      if (!forceNew && !intentEmail) {
        // Step 1: Try backend by deviceId (only when no intentEmail — intent email path goes straight to start).
        if (deviceInfo.deviceId) {
          try {
            const deviceSession = await onboardingService.getSessionByDevice(deviceInfo.deviceId);
            if (!abortController.signal.aborted && deviceSession?.sessionId) {
              console.log('[Onboarding] Found session by device ID, fetching details...', deviceSession.sessionId);

              try {
                const deviceSessionDetails = await onboardingService.getSessionDetails(deviceSession.sessionId);
                if (!abortController.signal.aborted && deviceSessionDetails) {
                  if (isSessionCompleted(deviceSessionDetails)) {
                    console.log('[Onboarding] Device session is completed; allowing fresh onboarding.', deviceSession.sessionId);
                    resetStateForNewSession();
                    forceCreateNew = true;
                    // Fall through to create new session — do not resume.
                  } else {
                    const sessionEmail =
                      (deviceSessionDetails.intentEmail ?? '').trim().toLowerCase() || getStoredSessionEmail();
                    if (deviceSessionDetails.intentEmail != null) {
                      console.log('[Onboarding] Session intentEmail from API (device resume):', deviceSessionDetails.intentEmail);
                    }
                    if (intentEmail && sessionEmail && intentEmail !== sessionEmail) {
                      console.log('[Onboarding] Intent email from home differs from session email; creating new session.', {
                        intentEmail: intentEmail.substring(0, 3) + '…',
                        sessionEmail: sessionEmail.substring(0, 3) + '…',
                      });
                      resetStateForNewSession();
                      forceCreateNew = true;
                      // Fall through to Step 3 to create new session (do not resume).
                    } else {
                      console.log('[Onboarding] Device session details loaded, pre-filling answers...', {
                        sessionId: deviceSessionDetails.sessionId,
                        responseCount: deviceSessionDetails.surveyResponses?.length || 0,
                      });
                      setSession(deviceSessionDetails);
                      storeSessionId(deviceSessionDetails.sessionId);
                      hydrateResponses(deviceSessionDetails, survey, { replace: true });
                      if (typeof deviceSessionDetails.currentStep === 'number') {
                        setCurrentIndex(clampIndex(deviceSessionDetails.currentStep));
                      }
                      return deviceSessionDetails;
                    }
                  }
                }
              } catch (detailsError) {
                console.warn('[Onboarding] Failed to load device session details, using basic session', detailsError);
                if (!abortController.signal.aborted) {
                  setSession(deviceSession);
                  storeSessionId(deviceSession.sessionId);
                  if (typeof deviceSession.currentStep === 'number') {
                    setCurrentIndex(clampIndex(deviceSession.currentStep));
                  }
                  return deviceSession;
                }
              }
            }
          } catch (deviceError: unknown) {
            // 404 = no session for this device. Do not immediately force new: try stored session ID first
            // so a transient 404 or replication lag doesn't abandon an in-progress session.
            if ((deviceError as { status?: number })?.status === 404) {
              console.log('[Onboarding] No active session for device (404). Will try stored session ID before creating new.');
            } else if (!abortController.signal.aborted) {
              console.warn('[Onboarding] Device lookup failed (non-404), falling back to stored sessionId', deviceError);
            }
          }
        }

        // Step 2 (fallback): Try to resume by stored session ID (even after device 404, to avoid abandoning session on transient 404).
        if (!forceCreateNew && storedSessionId) {
          try {
            const sessionStatus = await onboardingService.getSession(storedSessionId);
            if (!abortController.signal.aborted && sessionStatus?.sessionId) {
              console.log('[Onboarding] Session status check successful, fetching details...', sessionStatus.sessionId);

              try {
                const sessionDetails = await onboardingService.getSessionDetails(storedSessionId);
                if (!abortController.signal.aborted && sessionDetails) {
                  if (isSessionCompleted(sessionDetails)) {
                    console.log('[Onboarding] Stored session is completed; allowing fresh onboarding.', storedSessionId);
                    resetStateForNewSession();
                    forceCreateNew = true;
                    // Fall through to create new session — do not resume.
                  } else {
                    const sessionEmail =
                      (sessionDetails.intentEmail ?? '').trim().toLowerCase() || getStoredSessionEmail();
                    if (sessionDetails.intentEmail != null) {
                      console.log('[Onboarding] Session intentEmail from API (stored session resume):', sessionDetails.intentEmail);
                    }
                    if (intentEmail && sessionEmail && intentEmail !== sessionEmail) {
                      console.log('[Onboarding] Intent email from home differs from session email; creating new session.');
                      resetStateForNewSession();
                      forceCreateNew = true;
                      // Fall through to create new session.
                    } else {
                      console.log('[Onboarding] Session details loaded, pre-filling answers...', {
                        sessionId: sessionDetails.sessionId,
                        responseCount: sessionDetails.surveyResponses?.length || 0,
                      });
                      setSession(sessionDetails);
                      storeSessionId(sessionDetails.sessionId);
                      hydrateResponses(sessionDetails, survey, { replace: true });
                      if (typeof sessionDetails.currentStep === 'number') {
                        setCurrentIndex(clampIndex(sessionDetails.currentStep));
                      }
                      // Update device info if session exists and device changed, then update location (non-critical)
                      if (sessionDetails.deviceId !== deviceInfo.deviceId) {
                        try {
                          await onboardingService.updateDevice(sessionDetails.sessionId, deviceInfo as unknown as Record<string, unknown>);
                          console.log('[Onboarding] Device info updated for existing session');
                          await updateLocationBestEffort(sessionDetails.sessionId, deviceInfo);
                        } catch {
                          console.debug('[Onboarding] Device update failed (non-critical)');
                        }
                      }
                      return sessionDetails;
                    }
                  }
                }
              } catch (detailsError) {
                console.warn('[Onboarding] Failed to load session details, using status response', detailsError);
                if (!abortController.signal.aborted && sessionStatus) {
                  setSession(sessionStatus);
                  storeSessionId(sessionStatus.sessionId);
                  if (typeof sessionStatus.currentStep === 'number') {
                    setCurrentIndex(clampIndex(sessionStatus.currentStep));
                  }
                  return sessionStatus;
                }
              }
            }
          } catch (resumeError) {
            if (!abortController.signal.aborted) {
              console.warn('[Onboarding] Failed to resume by stored session ID, creating new session', resumeError);
            }
          }
        }
      }

      // Step 3: Create new session (as per guide Step 2)
      if (forceCreateNew || forceNew) {
        console.log('[Onboarding] Creating NEW session (forceNew or email changed). intentEmail:', intentEmail ?? '(none)');
      }
      // Sanitize device info to only include fields with valid values (filter out undefined/null)
      // API validation requires: userAgent max 100 chars, osVersion max 50 chars
      const rawUserAgent = deviceInfo.userAgent || (typeof window !== 'undefined' && window.navigator ? window.navigator.userAgent : '');
      const sanitizedUserAgent = rawUserAgent.length > 100 ? rawUserAgent.substring(0, 100) : rawUserAgent;
      
      // Extract or create a short osVersion (max 50 chars)
      // If osVersion is provided and valid, use it; otherwise extract from platform/userAgent or skip
      let sanitizedOsVersion: string | undefined;
      if (deviceInfo.osVersion && typeof deviceInfo.osVersion === 'string') {
        // Truncate osVersion to 50 chars if needed
        sanitizedOsVersion = deviceInfo.osVersion.length > 50 ? deviceInfo.osVersion.substring(0, 50) : deviceInfo.osVersion;
      } else if (typeof window !== 'undefined' && window.navigator) {
        // Extract OS info from navigator.platform (usually short like "MacIntel", "Win32", etc.)
        const platform = window.navigator.platform || '';
        if (platform && platform.length <= 50) {
          sanitizedOsVersion = platform;
        }
        // If platform is too long or not available, try to extract OS from userAgent
        else if (rawUserAgent) {
          // Try to extract OS name from user agent (e.g., "macOS", "Windows", "Linux")
          const osMatch = rawUserAgent.match(/(Mac OS X|Windows|Linux|Android|iOS)/i);
          if (osMatch && osMatch[0].length <= 50) {
            sanitizedOsVersion = osMatch[0];
          }
        }
      }
      
      // Build payload with all fields - backend accepts them all (tested with curl)
      const sanitizedDeviceInfo: DeviceInfo = {
        deviceId: deviceInfo.deviceId,
        deviceType: deviceInfo.deviceType || 'WEB',
        ipAddress: deviceInfo.ipAddress || '127.0.0.1',
        userAgent: sanitizedUserAgent,
      };
      
      // Add optional fields if they have valid non-empty string values
      if (deviceInfo.timezone && typeof deviceInfo.timezone === 'string') {
        sanitizedDeviceInfo.timezone = deviceInfo.timezone;
      }
      if (deviceInfo.language && typeof deviceInfo.language === 'string') {
        sanitizedDeviceInfo.language = deviceInfo.language;
      }
      if (deviceInfo.deviceModel && typeof deviceInfo.deviceModel === 'string') {
        sanitizedDeviceInfo.deviceModel = deviceInfo.deviceModel;
      }
      if (deviceInfo.deviceBrand && typeof deviceInfo.deviceBrand === 'string') {
        sanitizedDeviceInfo.deviceBrand = deviceInfo.deviceBrand;
      }
      if (deviceInfo.screenResolution && typeof deviceInfo.screenResolution === 'string') {
        sanitizedDeviceInfo.screenResolution = deviceInfo.screenResolution;
      }
      // Only include osVersion if we have a valid shortened version
      if (sanitizedOsVersion && sanitizedOsVersion.length <= 50) {
        sanitizedDeviceInfo.osVersion = sanitizedOsVersion;
      }
      if (deviceInfo.appVersion && typeof deviceInfo.appVersion === 'string') {
        sanitizedDeviceInfo.appVersion = deviceInfo.appVersion;
      }
      if (intentEmail) {
        sanitizedDeviceInfo.intentEmail = intentEmail;
      }
      // Tell backend to abandon old session when email changed or force-new requested
      if (forceCreateNew || forceNew) {
        sanitizedDeviceInfo.forceNew = true;
      }

      // IMPORTANT:
      // The backend rejects `locationInfo` on the initial start payload (403).
      // We create the session with device info only and update location separately
      // via `/api/v1/onboarding/{sessionId}/location` after session creation.
      const startPayload: StartOnboardingPayload = {
        ...sanitizedDeviceInfo,
      };

      console.log('[Onboarding] Creating new session with sanitized device info:', JSON.stringify(sanitizedDeviceInfo, null, 2));
      if (intentEmail) {
        console.log('[Onboarding] Sending intentEmail to backend:', intentEmail);
      }
      const useAuthForStart = showSubscriptionParam && isAuthContextAuthenticated;
      const sessionResponse = await onboardingService.startSession(startPayload, useAuthForStart ? { useAuth: true } : undefined);
      console.log('[Onboarding] Start API response:', JSON.stringify(sessionResponse, null, 2));

      const raw = ('data' in sessionResponse && typeof sessionResponse.data === 'object' && sessionResponse.data !== null ? sessionResponse.data : sessionResponse) as OnboardingStartResponse & Record<string, unknown>;
      const response = raw as OnboardingStartResponse;
      const topLevel = sessionResponse as unknown as Record<string, unknown>;

      // Scenario 1: User already onboarded — redirect to login (no session created)
      // Check both unwrapped response and top-level (backend may put action in either place)
      const isRedirectLogin =
        response?.action === 'REDIRECT_LOGIN' ||
        topLevel?.action === 'REDIRECT_LOGIN' ||
        (typeof topLevel?.action === 'string' && topLevel.action.toUpperCase() === 'REDIRECT_LOGIN') ||
        ((response?.redirectTo != null || topLevel?.redirectTo != null) && !response?.sessionId && !topLevel?.sessionId);

      if (isRedirectLogin) {
        // Returning from login to subscription (subscription=1 + authenticated): backend may still return REDIRECT_LOGIN; do not redirect again.
        if (showSubscriptionParam && isAuthContextAuthenticated) {
          return null;
        }
        if (!abortController.signal.aborted) {
          redirectToLoginRef.current = true;
          const loginPath = (response.redirectTo ?? topLevel?.redirectTo ?? '/login') as string;
          // Existing user: after login send to coach. Use backend next or default /coach. Only allow relative paths.
          let nextPath = (response.next ?? topLevel?.next ?? '/coach') as string;
          if (typeof nextPath !== 'string' || !nextPath.startsWith('/') || nextPath.startsWith('//')) {
            nextPath = '/coach';
          }
          navigate(`${loginPath}?next=${encodeURIComponent(nextPath)}`, { replace: true });
        }
        return null;
      }

      // Scenario 2 & 3: Session returned (resume or new)
      const sessionId = (response.sessionId || (sessionResponse as unknown as Record<string, unknown>).sessionId) as string | undefined;
      if (!sessionId || typeof sessionId !== 'string') {
        throw new Error('Session ID not found in API response. Response: ' + JSON.stringify(sessionResponse));
      }

      const sessionData = response as Partial<OnboardingSession>;
      if (sessionData?.intentEmail !== undefined && sessionData?.intentEmail !== null) {
        console.log('[Onboarding] Backend returned intentEmail:', sessionData.intentEmail);
      }

      const step = sessionData?.currentStep ?? 0;
      let sessionToUse: OnboardingSession = {
        sessionId,
        deviceId: sessionData?.deviceId || deviceInfo.deviceId,
        userId: sessionData?.userId ?? null,
        status: sessionData?.status || 'IN_PROGRESS',
        currentStep: step,
        totalSteps: sessionData?.totalSteps ?? 0,
        completionPercentage: sessionData?.completionPercentage ?? 0,
        isAuthenticated: sessionData?.isAuthenticated,
        authProvider: sessionData?.authProvider,
        surveyResponses: sessionData?.surveyResponses,
        message: sessionData?.message,
        expiresAt: sessionData?.expiresAt,
        updatedAt: sessionData?.updatedAt,
        createdAt: sessionData?.createdAt,
      };

      // Resumed session (currentStep > 0) may need details to hydrate survey answers
      if (step > 0 && (!sessionData?.surveyResponses || sessionData.surveyResponses.length === 0)) {
        try {
          const details = await onboardingService.getSessionDetails(sessionId);
          if (!abortController.signal.aborted && details?.surveyResponses?.length) {
            sessionToUse = { ...sessionToUse, ...details, surveyResponses: details.surveyResponses };
            hydrateResponses(sessionToUse, survey, { replace: true });
          }
        } catch (detailsErr) {
          console.warn('[Onboarding] Failed to fetch session details for resume:', detailsErr);
        }
      } else if (sessionData?.surveyResponses?.length) {
        hydrateResponses(sessionToUse, survey, { replace: true });
      }

      if (!abortController.signal.aborted) {
        setSession(sessionToUse);
        storeSessionId(sessionId);
        setStoredSessionEmail(intentEmail ?? getIntentEmail());
        setCurrentIndex(clampIndex(step));
        console.log('[Onboarding] Session stored:', sessionId, step > 0 ? '(resumed)' : '(new)');
        // Returning from login to subscription: show subscription step immediately
        if (useAuthForStart) {
          setSurveyCompleted(true);
          setShowAuth(false);
          setShowSubscriptionPlans(true);
        }

        (async () => {
          try {
            try {
              await onboardingService.updateDevice(sessionId, deviceInfo as unknown as Record<string, unknown>);
            } catch {
              try {
                await onboardingService.updateDevicePartial?.(sessionId, deviceInfo as unknown as Record<string, unknown>);
              } catch {
                /* ignore */
              }
            }
            try {
              await updateLocationBestEffort(sessionId, deviceInfo);
            } catch {
              /* ignore */
            }
          } catch {
            /* ignore */
          }
        })();
      }

      return sessionToUse;
    } catch (sessionError: unknown) {
      if (!abortController.signal.aborted) {
        const err = sessionError as { message?: string; status?: number };
        console.error('[Onboarding] Unable to start onboarding session', sessionError);
        console.error('[Onboarding] Session creation error details:', {
          message: err?.message,
          status: err?.status,
          details: (sessionError as { details?: unknown })?.details,
          response: (sessionError as { response?: unknown })?.response,
          stack: (sessionError as { stack?: string })?.stack,
        });
        
        // Try to extract validation error message
        let errorMessage = 'We could not create an onboarding session. Please check your network connection and try again.';
        if (err?.status === 400 && (sessionError as { details?: unknown })?.details) {
          const details = (sessionError as { details?: unknown }).details;
          if (typeof details === 'object' && details !== null) {
            if ('message' in details && typeof details.message === 'string') {
              errorMessage = details.message;
            } else if ('validationErrors' in details && Array.isArray(details.validationErrors) && details.validationErrors.length > 0) {
              errorMessage = `Validation error: ${details.validationErrors[0]}`;
            }
          }
        }
        
        setError(errorMessage);
      }
      return null;
    } finally {
      if (!abortController.signal.aborted) {
        setIsSessionLoading(false);
      }
    }
  }, [survey, updateLocationBestEffort, visibleQuestions.length, navigate, showSubscriptionParam, isAuthContextAuthenticated]);

  // Create/resume session when we have survey and intent email (from home URL, gate, or localStorage). Skip when on email gate with no email yet.
  useEffect(() => {
    if (!survey) return;
    if (!hasIntentEmail) return; // Wait for email (from home or gate submit) so start API receives intentEmail

    const abortController = new AbortController();
    const executeSession = async () => {
      try {
        const newSession = await createOrResumeSession();
        if (redirectToLoginRef.current) return; // Start API returned REDIRECT_LOGIN; we already navigated
        if (!newSession && !abortController.signal.aborted) {
          console.error('[Onboarding] Failed to create session on mount');
          setError('Unable to create onboarding session. Please refresh the page.');
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('[Onboarding] Error creating session on mount:', error);
          setError('Unable to create onboarding session. Please check your connection and refresh.');
        }
      }
    };

    executeSession();

    return () => {
      abortController.abort();
    };
  }, [survey, createOrResumeSession, hasIntentEmail]);

  const ensureActiveSession = useCallback(async () => {
    // If we already have a session with sessionId, return it
    if (session?.sessionId) {
      console.log('[Onboarding] Using existing session:', session.sessionId);
      return session;
    }

    const intentEmail = getIntentEmail();

    const clampIndex = (index: number) => {
      if (!Number.isFinite(index)) return 0;
      const max = Math.max(0, visibleQuestions.length - 1);
      return Math.min(Math.max(0, Math.floor(index)), max);
    };

    const resetStateForNewSession = () => {
      clearOnboardingPageStateCache(); // avoid stale onboarding UI cache across sessions
      setAnswers({});
      setCurrentIndex(0);
      setQuoteHistory(new Set());
      setSession(null);
    };

    // Source-of-truth: always prefer resuming by deviceId first.
    // Do not resume if the device's session is completed — allow fresh onboarding (e.g. different email).
    // Do not resume if intent email from home differs from session email (different user on same device).
    const deviceInfo = getBrowserDeviceInfo();
    if (deviceInfo.deviceId && !isSessionLoading) {
      try {
        const deviceSession = await onboardingService.getSessionByDevice(deviceInfo.deviceId);
        if (deviceSession?.sessionId) {
          try {
            const deviceSessionDetails = await onboardingService.getSessionDetails(deviceSession.sessionId);
            if (deviceSessionDetails?.sessionId) {
              if (isSessionCompleted(deviceSessionDetails)) {
                console.log('[Onboarding] Device session is completed; allowing fresh onboarding.', deviceSession.sessionId);
                resetStateForNewSession();
                // Fall through to createOrResumeSession (which will create new).
              } else {
                const sessionEmail =
                  (deviceSessionDetails.intentEmail ?? '').trim().toLowerCase() || getStoredSessionEmail();
                if (intentEmail && sessionEmail && intentEmail !== sessionEmail) {
                  console.log('[Onboarding] ensureActiveSession: intent email differs from session; forcing new session.');
                  resetStateForNewSession();
                  return createOrResumeSession({ forceNew: true });
                }
                console.log('[Onboarding] Resumed session by device ID with details:', deviceSessionDetails.sessionId);
                setSession(deviceSessionDetails);
                storeSessionId(deviceSessionDetails.sessionId);
                hydrateResponses(deviceSessionDetails, survey, { replace: true });
                if (typeof deviceSessionDetails.currentStep === 'number') {
                  setCurrentIndex(clampIndex(deviceSessionDetails.currentStep));
                }
                return deviceSessionDetails;
              }
            }
          } catch (detailsError) {
            console.warn('[Onboarding] Failed to load device session details, using basic session', detailsError);
            setSession(deviceSession);
            storeSessionId(deviceSession.sessionId);
            if (typeof deviceSession.currentStep === 'number') {
              setCurrentIndex(clampIndex(deviceSession.currentStep));
            }
            return deviceSession;
          }
        }
      } catch (deviceError: unknown) {
        if ((deviceError as { status?: number })?.status === 404) {
          console.log('[Onboarding] No active session for device (404). Will try stored session ID before creating new.');
        } else {
          console.warn('[Onboarding] Device lookup failed (non-404), falling back to stored sessionId', deviceError);
        }
      }
    }

    // Try stored session ID (e.g. after device 404 so we don't abandon an in-progress session).
    const storedSessionId = getStoredSessionId();
    if (storedSessionId && !isSessionLoading) {
      console.log('[Onboarding] Found stored sessionId, checking status and fetching details:', storedSessionId);
      try {
        const sessionStatus = await onboardingService.getSession(storedSessionId);
        if (sessionStatus?.sessionId) {
          try {
            const sessionDetails = await onboardingService.getSessionDetails(storedSessionId);
            if (sessionDetails?.sessionId) {
              if (isSessionCompleted(sessionDetails)) {
                console.log('[Onboarding] Stored session is completed; allowing fresh onboarding.', storedSessionId);
                resetStateForNewSession();
                // Fall through to createOrResumeSession (which will create new).
              } else {
                const sessionEmail =
                  (sessionDetails.intentEmail ?? '').trim().toLowerCase() || getStoredSessionEmail();
                if (intentEmail && sessionEmail && intentEmail !== sessionEmail) {
                  console.log('[Onboarding] ensureActiveSession: intent email differs from stored session; forcing new session.');
                  resetStateForNewSession();
                  return createOrResumeSession({ forceNew: true });
                }
                console.log('[Onboarding] Resumed session with details from stored ID:', sessionDetails.sessionId);
                setSession(sessionDetails);
                hydrateResponses(sessionDetails, survey, { replace: true });
                if (typeof sessionDetails.currentStep === 'number') {
                  setCurrentIndex(clampIndex(sessionDetails.currentStep));
                }
                return sessionDetails;
              }
            }
          } catch (detailsError) {
            console.warn('[Onboarding] Failed to load details, using status response', detailsError);
            if (sessionStatus) {
              setSession(sessionStatus);
              if (typeof sessionStatus.currentStep === 'number') {
                setCurrentIndex(clampIndex(sessionStatus.currentStep));
              }
              return sessionStatus;
            }
          }
        }
      } catch (error) {
        console.warn('[Onboarding] Failed to resume stored session, creating new one:', error);
      }
    }
    
    // If session is currently loading, wait a bit for it to complete
    if (isSessionLoading) {
      console.log('[Onboarding] Session is loading, waiting up to 5 seconds...');
      // Wait up to 5 seconds for session creation to complete
      for (let i = 0; i < 10; i++) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        // Check if session was set (we need to check localStorage since state might not update)
        const currentStoredId = getStoredSessionId();
        if (currentStoredId) {
          try {
            // Try to get details first
            const loadedSessionDetails = await onboardingService.getSessionDetails(currentStoredId);
            if (loadedSessionDetails?.sessionId) {
              console.log('[Onboarding] Session details loaded after waiting:', loadedSessionDetails.sessionId);
              setSession(loadedSessionDetails);
              hydrateResponses(loadedSessionDetails, survey, { replace: true });
              if (typeof loadedSessionDetails.currentStep === 'number') {
                setCurrentIndex(clampIndex(loadedSessionDetails.currentStep));
              }
              return loadedSessionDetails;
            }
          } catch (_error) {
            // Try status endpoint as fallback
            try {
              const loadedSession = await onboardingService.getSession(currentStoredId);
              if (loadedSession?.sessionId) {
                setSession(loadedSession);
                if (typeof loadedSession.currentStep === 'number') {
                  setCurrentIndex(clampIndex(loadedSession.currentStep));
                }
                return loadedSession;
              }
            } catch { /* Continue waiting */ }
          }
        }
      }
    }
    
    // Try to create/resume session
    console.log('[Onboarding] No active session found, creating/resuming...');
    const newSession = await createOrResumeSession();
    if (newSession?.sessionId) {
      console.log('[Onboarding] Session created/resumed:', newSession.sessionId);
      return newSession;
    }
    
    // If still no session, log error and return null
    console.error('[Onboarding] Failed to create or resume session after all attempts');
    return null;
  }, [createOrResumeSession, session, isSessionLoading, visibleQuestions.length, survey]);

  const isQuestionAnswered = (question: SurveyQuestion | null) => {
    if (!question) return false;
    
    // HEADING and BANNER types don't require answers - always considered "answered"
    if (question.type === 'HEADING' || question.type === 'BANNER') {
      return true;
    }
    
    const value = answers[question.id];

    // Check if question is required
    const isRequired = question.required ?? false;
    if (!isRequired) return true;

    // Handle MULTIPLE_CHOICE with validation rules (normalize like QuestionCard: array or "a,b" string)
    if (question.type === 'MULTIPLE_CHOICE') {
      let selections: string[];
      if (Array.isArray(value)) {
        selections = value.filter((s): s is string => typeof s === 'string' && s.length > 0);
      } else if (typeof value === 'string' && value.trim()) {
        selections = value.split(',').map((s) => s.trim()).filter(Boolean);
      } else {
        selections = [];
      }
      const minSelections = question.validationRules?.minSelections ?? 0;
      const maxSelections = question.validationRules?.maxSelections;

      if (selections.length < minSelections) return false;
      if (maxSelections && selections.length > maxSelections) return false;
      return selections.length > 0;
    }

    // Handle TEXT inputs with validation rules
    if (question.type === 'TEXT_SHORT' || question.type === 'TEXT_LONG' || question.type === 'EMAIL') {
      const textValue = typeof value === 'string' ? value.trim() : '';
      const minLength = question.validationRules?.minLength ?? 0;
      const maxLength = question.validationRules?.maxLength;
      
      if (textValue.length < minLength) return false;
      if (maxLength && textValue.length > maxLength) return false;
      
      // Email pattern validation
      if (question.type === 'EMAIL' && question.validationRules?.pattern) {
        try {
          const pattern = new RegExp(question.validationRules.pattern);
          if (!pattern.test(textValue)) return false;
        } catch {
          // Invalid pattern, skip validation
        }
      }
      
      return textValue.length > 0;
    }

    // Handle SINGLE_CHOICE
    if (question.type === 'SINGLE_CHOICE') {
      return value !== undefined && value !== null && value !== '';
    }

    return value !== undefined && value !== null && value !== '';
  };

  const findQuoteForAnswer = (question: SurveyQuestion, answer: string | string[]) => {
    if (!question.responseQuotes?.length) return null;
    const normalizedAnswer = Array.isArray(answer) ? answer : [answer];

    return question.responseQuotes.find((quote) => {
      if (quote.allResponses) return true;
      if (!quote.specificResponses?.length) return false;

      return normalizedAnswer.some((ans) => {
        if (!ans) return false;
        const ansStr = String(ans);
        // Try matching with option ID, value, or text
        const formatted = ansStr.toLowerCase().replace(/\s+/g, '_');
        const optionIdMatch = question.options?.find(
          opt => opt.id === ansStr || opt.value === ansStr || opt.text === ansStr
        );
        
        // Check if quote matches option ID, value, or formatted answer
        return (
          quote.specificResponses?.includes(ansStr) ||
          quote.specificResponses?.includes(formatted) ||
          (optionIdMatch && quote.specificResponses?.includes(optionIdMatch.id)) ||
          (optionIdMatch && optionIdMatch.value && quote.specificResponses?.includes(optionIdMatch.value))
        );
      });
    });
  };

  const buildResponsePayload = useCallback((
    question: SurveyQuestion,
    rawValue: string | string[]
  ): SurveyResponseRecord => {
    const answerType = answerTypeMap[question.type] || question.type;

    // Validate questionText - API requires this field. Prefer heading (e.g. "What's keeping you stuck?") when present.
    const heading = question.heading as { heading?: string; shortHeading?: string; text?: string } | undefined;
    const questionText =
      heading?.heading ||
      question.text ||
      heading?.shortHeading ||
      heading?.text ||
      question.id ||
      '';

    if (!questionText) {
      console.warn('[Onboarding] Warning: Question text is empty for question:', question.id);
    }

    // Base payload - match API spec exactly
    // API only accepts: surveyId, questionId, questionText, answer, answerType, metadata
    const basePayload: Omit<SurveyResponseRecord, 'answer'> = {
      surveyId: survey?.surveyId || DEFAULT_ONBOARDING_SURVEY_ID,
      questionId: question.id,
      questionText: questionText,
      answerType,
    };

    // Handle answer field based on question type
    let answer: string;

    // MULTIPLE_CHOICE: Send option.text (what we display in the UI), not option.value. Fall back to value if text empty.
    if (question.type === 'MULTIPLE_CHOICE') {
      let selections: string[];
      if (Array.isArray(rawValue)) {
        selections = rawValue.filter((s): s is string => typeof s === 'string' && s.length > 0);
      } else if (typeof rawValue === 'string' && rawValue.trim()) {
        selections = rawValue.split(',').map((s) => s.trim()).filter(Boolean);
      } else {
        selections = [];
      }
      const answerParts: string[] = [];
      const mapping: { selection: string; optionId?: string; used: string }[] = [];
      for (const sel of selections) {
        const option = question.options?.find(
          (opt) => opt.id === sel || opt.value === sel
        );
        if (!option) {
          answerParts.push(sel);
          mapping.push({ selection: sel, used: sel });
          continue;
        }
        const text = option.text != null && String(option.text).trim() !== '' ? String(option.text).trim() : '';
        const val = option.value != null && String(option.value).trim() !== '' ? String(option.value).trim() : '';
        const use = text || val;
        if (use.length > 0) {
          answerParts.push(use);
          mapping.push({ selection: sel, optionId: option.id, used: use });
        } else {
          console.warn('[Onboarding] MULTIPLE_CHOICE option has no text or value, skipping:', { questionId: question.id, optionId: option.id });
        }
      }
      answer = answerParts.join(',');
      console.log('[Onboarding] MULTIPLE_CHOICE payload (display text) mapping:', mapping, '-> answer:', answer);
    } else if (question.type === 'SINGLE_CHOICE') {
      const option = question.options?.find(
        (opt) => opt.id === rawValue || opt.value === rawValue
      );
      answer = option?.value || option?.text || String(rawValue || '');
    } else {
      // TEXT, NUMBER, EMAIL, etc.
      answer = typeof rawValue === 'string'
        ? rawValue
        : Array.isArray(rawValue)
        ? rawValue.join(',')
        : String(rawValue ?? '');
    }

    const payload = { ...basePayload, answer };
    if (question.type === 'MULTIPLE_CHOICE') {
      console.log('[Onboarding] buildResponsePayload MULTIPLE_CHOICE:', {
        questionId: question.id,
        questionText: payload.questionText,
        answer: payload.answer,
        rawValue,
      });
    }
    return payload;
  }, [survey?.surveyId]);

  const handleAnswerChange = (questionId: string, value: string | string[]) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  // Complete onboarding flow: Step 6 -> Step 7 -> Step 8
  // Note: Step 5 (consents/preferences) + Step 6 (complete payload) are handled in AuthPage.
  // This function ensures the session is completed, then polls and fetches the generated data.
  const handleCompleteFlow = useCallback(async (sessionId: string) => {
    setIsProcessing(true);
    setProcessingProgress(0);

    console.log('[Onboarding] 🚀 Starting completion flow for session:', sessionId);
    console.log('[Onboarding] Note: Complete payload is submitted in AuthPage');

    try {
      const isLocalModeEnabled = isLocalMode();

      // Step 6: Ensure onboarding is completed (AuthPage should have already called /complete with required payload).
      setProcessingProgress(20);
      let completedSession: OnboardingSession | null = null;

      // If already completed, do NOT call complete again.
      const details = await onboardingService.getSessionDetails(sessionId);
      const detailsWithCompletion = details as OnboardingSession & { completedAt?: string };
      const alreadyCompleted = !!detailsWithCompletion?.completedAt || !!detailsWithCompletion?.userId;
      if (alreadyCompleted) {
        console.log('[Onboarding] Step 6: Session already completed, skipping complete API call.', {
          completedAt: detailsWithCompletion?.completedAt,
          userId: detailsWithCompletion?.userId,
        });
        completedSession = details;
      } else {
        // If not completed yet, we need the exact completion payload saved by AuthPage.
        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(COMPLETE_REQUEST_STORAGE_KEY) : null;
        if (!raw) {
          throw new Error('Missing completion payload. Please return to the previous step and click “Complete Setup” again.');
        }
        let payload: UserOnboardingRequest;
        try {
          payload = JSON.parse(raw) as UserOnboardingRequest;
        } catch {
          throw new Error('Invalid completion payload. Please return to the previous step and click “Complete Setup” again.');
        }

        console.log('[Onboarding] Step 6: Completing onboarding using saved payload...');
        try {
          completedSession = await onboardingService.complete(sessionId, payload);
        } catch (err: unknown) {
          // Local dev setup: backend may require auth for /complete.
          // Requirement: allow local flow without tokens. If 403, finish UI locally.
          const error = err as { status?: number; message?: string };
          if (isLocalModeEnabled && error?.status === 403) {
            console.warn('[Onboarding] Local mode: /complete returned 403. Skipping server completion + AI generation.', error);

            setProcessingProgress(100);
            setCompletionData({
              userProfile: {
                firstName: payload?.firstName,
                lastName: payload?.lastName,
                email: payload?.email,
              },
              bio: {
                summary:
                  'Local mode: backend /complete is forbidden (403) in this environment. ' +
                  'We skipped server-side completion and AI generation. ' +
                  'Your survey responses are still saved to the session.',
              },
              learningPath: {
                title: 'Local mode (no server completion)',
                description:
                  'Completion and AI-generated outputs are unavailable because the backend /complete endpoint returned 403.',
              },
            });
            setIsProcessing(false);
            setIsCompleted(true);
            return;
          }
          throw error;
        }
        console.log('[Onboarding] Step 6: Complete API response:', completedSession);
      }

      const userId = completedSession?.userId || (details as OnboardingSession & { completedAt?: string })?.userId;
      if (!userId) {
        console.warn('[Onboarding] Complete API did not return userId, response:', completedSession);
        // Continue anyway - user might already have a profile
        console.log('[Onboarding] Continuing without userId...');
      } else {
        console.log('[Onboarding] Step 6: UserId received:', userId);
      }

      // Step 7: Poll session status until AI generation is complete (only if we have userId)
      setProcessingProgress(40);
      let aiGenerationComplete = false;
      
      if (userId) {
        console.log('[Onboarding] Step 7: Polling for AI generation status...');
        let pollAttempts = 0;
        const maxPollAttempts = 15; // 15 attempts * 2 seconds = 30 seconds max

        while (!aiGenerationComplete && pollAttempts < maxPollAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 2000)); // 2 second intervals
          
          try {
            const sessionStatus = await onboardingService.getSession(sessionId);
            const aiStatus = (sessionStatus as OnboardingSession & { aiGenerationStatus?: { bioGenerated?: boolean; learningPathGenerated?: boolean } })?.aiGenerationStatus;
            
            if (aiStatus?.bioGenerated && aiStatus?.learningPathGenerated) {
              aiGenerationComplete = true;
              setProcessingProgress(90);
              console.log('[Onboarding] Step 7: AI generation complete');
              break;
            }
            
            // Update progress based on generation status
            if (aiStatus?.bioGenerated) {
              setProcessingProgress(70);
            } else if (aiStatus?.learningPathGenerated === false) {
              setProcessingProgress(50);
            }
          } catch (pollError) {
            console.warn('[Onboarding] Step 7: Polling session status failed:', pollError);
          }
          
          pollAttempts++;
        }
        
        if (!aiGenerationComplete) {
          console.warn('[Onboarding] Step 7: AI generation timeout, continuing anyway...');
        }
      } else {
        // No userId - skip polling and fetching user data
        console.log('[Onboarding] Step 7: Skipping AI generation polling (no userId)');
        setProcessingProgress(90);
      }

      // Step 8: Fetch user profile, bio, and learning path (parallel) - only if we have userId
      setProcessingProgress(90);
      let profileResponse = null;
      let bioResponse = null;
      let learningPathResponse = null;
      
      if (userId) {
        console.log('[Onboarding] Step 8: Fetching user profile, bio, and learning path...');
        try {
          [profileResponse, bioResponse, learningPathResponse] = await Promise.all([
            onboardingService.getUserProfile(userId).catch((err) => {
              console.warn('[Onboarding] Failed to fetch profile:', err);
              return null;
            }),
            onboardingService.getUserBio(userId).catch((err) => {
              console.warn('[Onboarding] Failed to fetch bio:', err);
              return null;
            }),
            onboardingService.getUserLearningPath(userId).catch((err) => {
              console.warn('[Onboarding] Failed to fetch learning path:', err);
              return null;
            }),
          ]);
          console.log('[Onboarding] Step 8: Data fetched:', {
            hasProfile: !!profileResponse,
            hasBio: !!bioResponse,
            hasLearningPath: !!learningPathResponse,
          });
        } catch (fetchError) {
          console.error('[Onboarding] Step 8: Error fetching user data:', fetchError);
          // Continue anyway - partial data is better than none
        }
      } else {
        console.log('[Onboarding] Step 8: Skipping user data fetch (no userId)');
      }

      setProcessingProgress(100);

      // Resolve user email for redirect to learning path / user profile page
      const completionSession = completedSession as OnboardingSession & { intentEmail?: string };
      const detailsSession = detailsWithCompletion as OnboardingSession & { intentEmail?: string };
      const profileWithEmail = profileResponse as { email?: string } | null;
      let userEmailForPath =
        profileWithEmail?.email ??
        completionSession?.intentEmail ??
        detailsSession?.intentEmail ??
        getIntentEmail() ??
        getStoredSessionEmail();
      if (!userEmailForPath && typeof window !== 'undefined') {
        try {
          const raw = window.localStorage.getItem(COMPLETE_REQUEST_STORAGE_KEY);
          if (raw) {
            const payload = JSON.parse(raw) as UserOnboardingRequest;
            userEmailForPath = payload?.email ?? null;
          }
        } catch {
          // ignore
        }
      }
      if (userEmailForPath?.trim()) {
        navigate(`/learning-path/${encodeURIComponent(userEmailForPath.trim())}`, { replace: true });
      }

      // Wait a bit for final animation (only if not navigating)
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Set completion data (keeps state consistent if user returns to /onboarding)
      setCompletionData({
        userProfile: profileResponse || {},
        bio: bioResponse || {},
        learningPath: learningPathResponse || {},
      });

      setIsProcessing(false);
      setIsCompleted(true);
    } catch (completeError: unknown) {
      const err = completeError as { message?: string; status?: number; details?: unknown; stack?: string };
      console.error('[Onboarding] Complete flow failed:', completeError);
      console.error('[Onboarding] Error details:', {
        message: err?.message,
        status: err?.status,
        response: (completeError as { response?: unknown })?.response,
        stack: err?.stack,
      });
      
      setIsProcessing(false);
      
      // Create user-friendly error message
      let errorMessage = 'We encountered an error while finalizing your profile. Your responses have been saved.';
      
      if (err?.message?.includes('Authentication required')) {
        errorMessage = 'Please sign in to complete your onboarding. Your responses have been saved.';
      } else if (err?.message) {
        errorMessage = `${errorMessage} Error: ${err.message}`;
      }
      
      setError(errorMessage);
      
      // Still show completion page even on error - user has completed the survey
      // Set minimal completion data so the page can display
      setCompletionData({
        userProfile: {},
        bio: {},
        learningPath: {},
      });
      setIsCompleted(true);
    }
  }, [navigate]);

  const handleNext = async () => {
    if (!currentQuestion || !survey) return;
    
    // HEADING type - automatically advance without requiring answer
    if (currentQuestion.type === 'HEADING') {
      console.log('[Onboarding] HEADING type question - auto-advancing', {
        questionId: currentQuestion.id,
        questionText: currentQuestion.text,
      });
      if (currentIndex >= visibleQuestions.length - 1) {
        // Survey is complete - show auth page
        console.log('[Onboarding] ✅ Survey completed (HEADING on last question)! Showing auth page...');
        setSurveyCompleted(true);
        setShowAuth(true);
      } else {
        setCurrentIndex((prev) => Math.min(prev + 1, visibleQuestions.length - 1));
      }
      return;
    }
    
    // BANNER type - show banner overlay, don't require answer
    if (currentQuestion.type === 'BANNER') {
      console.log('[Onboarding] BANNER type question - showing banner overlay', {
        questionId: currentQuestion.id,
        questionText: currentQuestion.text,
      });
      setActiveBanner(currentQuestion);
      return;
    }
    
    // For other types, check if question is answered
    if (!isQuestionAnswered(currentQuestion)) return;

    // Ensure we have an active session before proceeding
    console.log('[Onboarding] handleNext: Ensuring active session...', { 
      hasSession: !!session?.sessionId, 
      isSessionLoading,
      storedSessionId: getStoredSessionId() 
    });
    
    const activeSession = await ensureActiveSession();
    if (!activeSession || !activeSession.sessionId) {
      const errorMsg = 'Unable to submit responses because there is no active session. Please refresh the page and try again.';
      console.error('[Onboarding] handleNext: No active session', { 
        activeSession, 
        session, 
        storedSessionId: getStoredSessionId() 
      });
      setError(errorMsg);
      // Try to create session one more time
      try {
        const retrySession = await createOrResumeSession();
        if (retrySession?.sessionId) {
          setError(null);
          // Retry the submission
          setTimeout(() => handleNext(), 100);
          return;
        }
      } catch (retryError) {
        console.error('[Onboarding] Retry session creation failed:', retryError);
      }
      return;
    }

    const answerValue = answers[currentQuestion.id];
    
    // Check if question requires an answer
    const isRequired = currentQuestion.required ?? false;
    
    // Only validate input if question is required
    // For non-required questions, allow continuing without an answer
    if (isRequired) {
      // Double-check: Don't submit if required question has no actual user input
      if (!answerValue || 
          (typeof answerValue === 'string' && !answerValue.trim()) ||
          (Array.isArray(answerValue) && answerValue.length === 0)) {
        console.warn('[Onboarding] Skipping API call - required question has no user input', {
          questionId: currentQuestion.id,
          answerValue,
          required: isRequired
        });
        return;
      }
    } else {
      // For non-required questions without input, check for quotes first, then advance
      if (!answerValue || 
          (typeof answerValue === 'string' && !answerValue.trim()) ||
          (Array.isArray(answerValue) && answerValue.length === 0)) {
        console.log('[Onboarding] Non-required question with no input - checking for quotes before advancing', {
          questionId: currentQuestion.id,
          required: isRequired
        });
        
        // Check for quotes even if no answer (some quotes might be for allResponses or empty answers)
        const quote = findQuoteForAnswer(currentQuestion, answerValue || '');
        if (quote) {
          console.log('[Onboarding] 📝 Found quote for empty/non-required answer, showing quote overlay', {
            quoteId: quote.id,
            headingQuote: quote.headingQuote,
          });
          if (!quoteHistory.has(quote.id)) {
            setQuoteHistory((prev) => new Set(prev).add(quote.id));
          }
          // Always show quote for motivation
          setActiveQuote(quote);
          return;
        }
        
        // No quote found, just advance to next question without submitting
        if (currentIndex >= visibleQuestions.length - 1) {
          // Survey is complete - show auth page
          console.log('[Onboarding] ✅ Survey completed (no input on last question)! Showing auth page...');
          setSurveyCompleted(true);
          setShowAuth(true);
        } else {
          setCurrentIndex((prev) => Math.min(prev + 1, visibleQuestions.length - 1));
        }
        return;
      }
    }
    
    // Check for quotes BEFORE API submission - show quotes immediately after answering
    // This ensures quotes are shown even if API call fails or is skipped
    const foundQuote = findQuoteForAnswer(currentQuestion, answerValue);
    if (foundQuote) {
      console.log('[Onboarding] ✅ Found matching quote BEFORE API submission:', {
        questionId: currentQuestion.id,
        quoteId: foundQuote.id,
        headingQuote: foundQuote.headingQuote,
        quoteText: foundQuote.quoteText,
        allResponses: foundQuote.allResponses,
        specificResponses: foundQuote.specificResponses,
        alreadyInHistory: quoteHistory.has(foundQuote.id),
      });
      // Always show quote for motivation (even if seen before)
      if (!quoteHistory.has(foundQuote.id)) {
        setQuoteHistory((prev) => new Set(prev).add(foundQuote.id));
      }
      // Show quote overlay immediately - don't wait for API submission
      setActiveQuote(foundQuote);
      setSubmitting(false);
      // Don't submit API call - show quote overlay first, user will continue after viewing quote
      console.log('[Onboarding] 📝 Quote overlay will be shown - API submission will happen after user continues from quote');
      return;
    } else {
      console.log('[Onboarding] ❌ No matching quote found for answer before API submission', {
        questionId: currentQuestion.id,
        questionText: currentQuestion.text,
        answerValue,
        hasResponseQuotes: !!currentQuestion.responseQuotes?.length,
        responseQuotesCount: currentQuestion.responseQuotes?.length || 0,
        availableQuotes: currentQuestion.responseQuotes?.map(q => ({
          id: q.id,
          allResponses: q.allResponses,
          specificResponses: q.specificResponses,
        })) || [],
      });
    }
    
    const payload = buildResponsePayload(currentQuestion, answerValue);
    
    // Validate payload before sending - ensure answer field has content
    if (!payload.answer || (typeof payload.answer === 'string' && !payload.answer.trim())) {
      console.warn('[Onboarding] Skipping API call - payload answer is empty', {
        questionId: payload.questionId,
        answer: payload.answer
      });
      return;
    }
    
    console.log('[Onboarding] PATCH payload (submitResponses):', {
      sessionId: activeSession.sessionId,
      payload: {
        surveyId: payload.surveyId,
        questionId: payload.questionId,
        questionText: payload.questionText,
        answer: payload.answer,
        answerType: payload.answerType,
      },
    });

    setSubmitting(true);
    setError(null);
    
    // Retry logic for submitting responses
    let retryCount = 0;
    const maxRetries = 3;
    let lastError: unknown = null;
    
    while (retryCount < maxRetries) {
      try {
        const updatedSession = await onboardingService.submitResponses(
          activeSession.sessionId,
          [payload]
        );
        
        // Handle different response structures
        const sessionData = ('data' in updatedSession && typeof updatedSession.data === 'object' && updatedSession.data !== null ? updatedSession.data : updatedSession) as OnboardingSession;
        if (sessionData?.sessionId) {
          setSession(sessionData);
        } else {
          setSession(updatedSession);
        }

        // Quote check was already done before API submission
        // If quote was found, it's already being shown in overlay
        // Just proceed to next question or completion
        if (activeQuote) {
          console.log('[Onboarding] Quote is already being shown, waiting for user to continue from quote');
          setSubmitting(false);
          return; // Quote overlay is showing, wait for user to click continue
        }

        if (currentIndex >= visibleQuestions.length - 1) {
          // Survey is complete - show auth page
          console.log('[Onboarding] ✅ Survey completed! Showing auth page...');
          setSurveyCompleted(true);
          setShowAuth(true);
        } else {
          setCurrentIndex((prev) => Math.min(prev + 1, visibleQuestions.length - 1));
        }
        setSubmitting(false);
        return; // Success, exit retry loop
      } catch (submitError) {
        lastError = submitError;
        retryCount++;
        
        if (retryCount < maxRetries) {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          continue;
        }
      }
    }
    
    // All retries failed
    console.error('Failed to submit survey responses after retries', lastError);
    setError(
      'We could not save your response. Please check your network connection and try again later.'
    );
    setSubmitting(false);
  };

  const handleBannerContinue = useCallback(() => {
    const question = activeBanner;
    setActiveBanner(null);

    if (!question) return;

    // Optional: jump to a specific question by id (metadata.ctaLeadsTo)
    const ctaLeadsTo = (question.metadata as { ctaLeadsTo?: string } | undefined)?.ctaLeadsTo;
    if (ctaLeadsTo) {
      const targetIndex = visibleQuestions.findIndex((q) => q.id === ctaLeadsTo);
      if (targetIndex >= 0) {
        setCurrentIndex(targetIndex);
        return;
      }
    }

    // Otherwise advance to next question
    if (currentIndex >= visibleQuestions.length - 1) {
      setSurveyCompleted(true);
      setShowAuth(true);
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, visibleQuestions.length - 1));
    }
  }, [activeBanner, currentIndex, visibleQuestions]);

  const handleBannerSecondary = useCallback(() => {
    setActiveBanner(null);
    setSurveyCompleted(true);
    setShowAuth(true);
  }, []);

  const handleQuoteContinue = async () => {
    // When user clicks continue on quote, we need to submit the answer to API if not already submitted
    const quoteQuestion = visibleQuestions[currentIndex];
    const quoteAnswer = answers[quoteQuestion?.id];
    
    if (quoteQuestion && quoteAnswer) {
      console.log('[Onboarding] Quote continue - submitting answer to API if not already submitted', {
        questionId: quoteQuestion.id,
        answer: quoteAnswer,
      });
      
      // Submit answer to API if we haven't already (quote check happened before API submission)
      try {
        const activeSession = await ensureActiveSession();
        if (activeSession?.sessionId && quoteAnswer) {
          const payload = buildResponsePayload(quoteQuestion, quoteAnswer);
          if (payload.answer && (typeof payload.answer !== 'string' || payload.answer.trim())) {
            console.log('[Onboarding] PATCH payload (quote continue):', {
              sessionId: activeSession.sessionId,
              payload: { questionId: payload.questionId, questionText: payload.questionText, answer: payload.answer, answerType: payload.answerType },
            });
            await onboardingService.submitResponses(activeSession.sessionId, [payload]);
            console.log('[Onboarding] Answer submitted successfully after quote');
          }
        }
      } catch (error) {
        console.error('[Onboarding] Failed to submit answer after quote continue (non-critical):', error);
        // Don't block user - continue anyway
      }
    }
    
    // Clear quote and proceed
    setActiveQuote(null);
    if (currentIndex >= visibleQuestions.length - 1) {
      // Survey is complete - show auth page when finishing via quote continue
      console.log('[Onboarding] ✅ Survey completed (via quote continue)! Showing auth page...');
      setSurveyCompleted(true);
      setShowAuth(true);
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, visibleQuestions.length - 1));
    }
  };

  const handleBack = async () => {
    if (activeQuote) {
      setActiveQuote(null);
      return;
    }
    if (activeBanner) {
      setActiveBanner(null);
      return;
    }
    if (currentIndex === 0) return;

    // Submit current question's answer before going back (e.g. reselect → Back)
    const prevQuestion = visibleQuestions[currentIndex];
    const prevAnswer = prevQuestion ? answers[prevQuestion.id] : undefined;
    const hasAnswer = prevAnswer !== undefined && prevAnswer !== null &&
      (typeof prevAnswer === 'string' ? prevAnswer.trim() !== '' : (Array.isArray(prevAnswer) && prevAnswer.length > 0));
    if (prevQuestion && hasAnswer) {
      try {
        const activeSession = await ensureActiveSession();
        if (activeSession?.sessionId) {
          const payload = buildResponsePayload(prevQuestion, prevAnswer);
          if (payload.answer && (typeof payload.answer !== 'string' || payload.answer.trim())) {
            console.log('[Onboarding] PATCH payload (back):', {
              sessionId: activeSession.sessionId,
              payload: { questionId: payload.questionId, questionText: payload.questionText, answer: payload.answer, answerType: payload.answerType },
            });
            await onboardingService.submitResponses(activeSession.sessionId, [payload]);
          }
        }
      } catch (e) {
        console.debug('[Onboarding] Submit on back failed (non-blocking):', e);
      }
    }

    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  // Auto-save functionality (debounced)
  useEffect(() => {
    if (!currentQuestion || !session || submitting || !survey) return;
    
    const answerValue = answers[currentQuestion.id];
    if (answerValue === undefined || answerValue === null || answerValue === '') return;
    if (Array.isArray(answerValue) && answerValue.length === 0) return;
    
    const autoSaveTimer = setTimeout(async () => {
      try {
        const payload = buildResponsePayload(currentQuestion, answerValue);
        console.log('[Onboarding] PATCH payload (auto-save):', {
          sessionId: session.sessionId,
          payload: { questionId: payload.questionId, questionText: payload.questionText, answer: payload.answer, answerType: payload.answerType },
        });
        await onboardingService.submitResponses(session.sessionId, [payload]);
        // Silently update session - don't show loading state
      } catch (error) {
        // Silently fail - auto-save shouldn't interrupt user flow
        console.debug('Auto-save failed:', error);
      }
    }, 2000); // Auto-save after 2 seconds of inactivity

    return () => clearTimeout(autoSaveTimer);
  }, [answers, currentQuestion, session, submitting, survey, buildResponsePayload]);

  const resetSession = () => {
    // Per requirement: do NOT clear sessionId. Only reset what's shown/selected on the page.
    clearOnboardingPageStateCache();

    // Reset in-memory UI state (keep the session object so we don't rehydrate from server immediately)
    setError(null);
    setAnswers({});
    setCurrentIndex(0);
    setActiveQuote(null);
    setActiveBanner(null);
    setQuoteHistory(new Set());
    setSubmitting(false);
    setSurveyCompleted(false);
    setShowAuth(false);
    setShowSubscriptionPlans(false);
    setShowConsent(false);
    setIsProcessing(false);
    setProcessingProgress(0);
    setIsCompleted(false);
    setCompletionData(null);

    // Reset progress UI derived from session (but keep sessionId).
    setSession((prev) =>
      prev
        ? {
            ...prev,
            currentStep: 0,
            completionPercentage: 0,
            status: 'IN_PROGRESS',
          }
        : prev
    );
  };

  const renderContent = () => {
    if (isLoadingSurvey) {
      return (
        <div className="flex flex-col items-center py-16 px-4">
          <BrandedLoading message="Loading personalized onboarding…" iconSize="lg" />
        </div>
      );
    }

    if (!survey) {
      return (
        <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-6 text-destructive-foreground">
          <p className="font-semibold">Unable to load onboarding survey.</p>
          <p className="mt-2 text-sm">
            Please verify that the user-profile service is running on
            <code className="mx-2 rounded bg-background px-2 py-1 text-xs">
              {import.meta.env.FIVE_USER_PROFILE_SERVICE_URL}
            </code>
            and that your network allows access.
          </p>
          <Button 
            className="mt-4" 
            onClick={() => {
              const abortController = new AbortController();
              loadSurvey(abortController);
            }}
          >
            Retry
          </Button>
        </div>
      );
    }

    if (isCompleted) {
      return (
        <Card className="shadow-xl border-border/50 bg-card/95 backdrop-blur-sm animate-scale-in">
          <CardHeader className="text-center space-y-4 pb-6">
            <div className="mx-auto w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <span className="text-4xl">🎉</span>
            </div>
            <CardTitle className="text-3xl font-bold text-foreground">
              You're all set!
            </CardTitle>
            <CardDescription className="text-base max-w-lg mx-auto">
              You've completed the onboarding survey. Your responses have been saved and we're ready to create your personalized career coaching experience.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {session?.completionPercentage === 100 ? (
              <>
                <div className="rounded-xl bg-muted/50 p-6 space-y-3">
                  <p className="font-semibold text-foreground">What's next?</p>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <span>Authenticate or link your identity provider</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <span>Complete your profile information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <span>Review AI-generated bio and personalized learning path</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <span>Confirm subscription preference and finish setup</span>
                    </li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button 
                    variant="primary" 
                    size="lg"
                    className="flex-1 sm:flex-initial"
                    onClick={() => {
                      // Navigate to authentication/completion flow when ready
                      alert('Completion flow with authentication will be implemented here');
                    }}
                  >
                    Continue to Account Creation →
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={resetSession}
                    className="sm:order-first"
                  >
                    Start Over
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => window.location.replace('/')}
                  >
                    Return Home
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-foreground font-medium">Next steps:</p>
                <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
                  <li>Authenticate or link your identity provider</li>
                  <li>Review AI-generated bio and personalized learning path</li>
                  <li>Confirm subscription preference and finish setup</li>
                </ul>
                <div className="flex flex-wrap gap-3 pt-4">
                  <Button variant="primary" onClick={resetSession}>
                    Start Over
                  </Button>
                  <Button variant="outline" onClick={() => window.location.replace('/') }>
                    Return Home
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      );
    }

    if (!currentQuestion) {
      return null;
    }

    // Show banner overlay if active (BANNER type: message only, no user input; CTAs from metadata)
    if (activeBanner) {
      const bannerMeta = activeBanner.metadata as { ctaSecondary?: string } | undefined;
      return (
        <BannerCard
          question={activeBanner}
          onContinue={handleBannerContinue}
          onSecondaryClick={bannerMeta?.ctaSecondary ? handleBannerSecondary : undefined}
        />
      );
    }

    // Show quote overlay if active
    if (activeQuote) {
      return <ResponseQuoteCard quote={activeQuote} onContinue={handleQuoteContinue} />;
    }

    return (
      <div className="space-y-6">
        <div className="animate-scale-in">
          <QuestionCard
            question={currentQuestion}
            value={answers[currentQuestion.id]}
            onChange={(value) => handleAnswerChange(currentQuestion.id, value)}
          />
        </div>
        
        {/* Enhanced Navigation */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentIndex === 0 || submitting}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            ← Back
          </Button>
          <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto">
            <Button
              variant="ghost"
              onClick={resetSession}
              disabled={isSessionLoading || submitting}
              className="hidden sm:inline-flex"
              size="sm"
            >
              Reset
            </Button>
            <Button
              variant="primary"
              onClick={handleNext}
              isLoading={submitting || isSessionLoading}
              disabled={!isQuestionAnswered(currentQuestion) || submitting || isSessionLoading}
              className="flex-1 sm:flex-initial min-w-[120px]"
              size="lg"
            >
              {isSessionLoading
                ? 'Loading…'
                : currentIndex === visibleQuestions.length - 1
                  ? 'Complete'
                  : 'Continue →'}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // Show final completion page (user profile & learning path) after processing
  if (isCompleted) {
    const sessionIdToShow = session?.sessionId || getStoredSessionId();
    const emailForCompletion = buildingEmailRef.current ?? undefined; // so Welcome page has user email even when completionData is empty (e.g. timeout)
    return (
      <OnboardingCompletionPage
        userProfile={completionData?.userProfile as UserProfile | undefined}
        bio={completionData?.bio as Bio | undefined}
        learningPath={completionData?.learningPath as LearningPlan | undefined}
        sessionId={sessionIdToShow || undefined}
        initialUserEmail={emailForCompletion}
      />
    );
  }

  // Building phase: after consent we show a single stable "Building..." screen and poll here until profile+bio and path are ready (or timeout), then show Welcome page with data
  if (profileStepEmail) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-primary/5 to-violet-500/10 dark:via-primary/10 dark:to-violet-500/20 px-4">
          <div className="w-full max-w-2xl">
            <Card className="border-border/60 bg-card/85 backdrop-blur-sm shadow-xl overflow-hidden">
              <div className="p-6 sm:p-8 bg-[radial-gradient(ellipse_80%_55%_at_30%_0%,hsl(var(--primary)/0.18),transparent_55%)]">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-center sm:justify-start mb-3">
                    <BrandedSpinner sizeClassName="h-12 w-12" className="shrink-0" />
                  </div>
                  <CardTitle className="text-2xl sm:text-3xl">
                    Setting up your experience
                  </CardTitle>
                  <CardDescription className="text-sm sm:text-base">
                    {BUILDING_MESSAGES[buildMessageIndex]}
                  </CardDescription>
                  <p className="text-xs text-muted-foreground mt-2">
                    This usually takes 1–2 minutes. You&apos;ll land on your Welcome page as soon as your profile, AI bio, and learning path are ready.
                  </p>
                </CardHeader>

                <CardContent className="space-y-6 pb-8">
                  {/* Progress */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-muted-foreground">Overall progress</p>
                      <p className="text-sm font-semibold tabular-nums text-primary">{buildPercent}%</p>
                    </div>
                    <div className="h-3 w-full rounded-full bg-muted/80 border border-border/60 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-primary via-primary to-violet-500 shadow-lg"
                        style={{ width: `${buildPercent}%`, transition: 'width 350ms ease' }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                      <span>Polling every 3 seconds</span>
                      <span>Live updates</span>
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="grid gap-3 sm:grid-cols-3">
                    {[
                      { key: 'profile', label: 'Profile created', done: buildProfileReady, hint: 'Step 1' },
                      { key: 'bio', label: 'AI bio generated', done: buildBioReady, hint: 'Step 2' },
                      { key: 'path', label: 'Learning path ready', done: buildPathReady, hint: 'Step 3' },
                    ].map((s) => (
                      <div key={s.key} className="rounded-2xl border border-border/50 bg-muted/15 px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={[
                              'h-10 w-10 rounded-xl flex items-center justify-center ring-1',
                              s.done ? 'bg-emerald-500/15 ring-emerald-500/30' : 'bg-background ring-border/60',
                            ].join(' ')}
                          >
                            {s.done ? (
                              <span className="text-emerald-600 text-lg font-bold">✓</span>
                            ) : (
                              <div className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">{s.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {s.done ? 'Completed' : 'In progress'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Fancy activity log */}
                  <div className="rounded-2xl border border-border/60 bg-background/40 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Activity log</p>
                      <p className="text-xs text-muted-foreground">Last {Math.min(buildLogs.length, 6)} updates</p>
                    </div>
                    <div className="mt-3 max-h-28 overflow-auto pr-1 space-y-1">
                      {buildLogs.length === 0 ? (
                        <div className="text-xs text-muted-foreground">Waiting for updates...</div>
                      ) : (
                        buildLogs.slice(-6).map((line, i) => (
                          <div key={`${line}-${i}`} className="text-xs text-muted-foreground leading-relaxed">
                            {line}
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="flex justify-center pt-2">
                    <BrandedLoading
                      message="Don't refresh — we're almost done."
                      iconSize="sm"
                      layout="inline"
                      className="text-xs text-muted-foreground [&_p]:text-xs"
                    />
                  </div>
                </CardContent>
              </div>
            </Card>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // Show subscription plan selection page after auth (BEFORE consent/completion). No second login — user just registered.
  const subscriptionSessionId = session?.sessionId || getStoredSessionId();
  if (showSubscriptionPlans && subscriptionSessionId) {
    return (
      <SubscriptionPlanSelectionPage
        sessionId={subscriptionSessionId}
        onPlanSelected={async (planId) => {
          console.log('[Onboarding] Plan selected and saved:', planId);
          // No-op: selection is persisted server-side; we advance on onContinue
        }}
        onContinue={() => {
          // After plan is selected, go directly to consent (payment comes after profile is built)
          console.log('[Onboarding] Proceeding to consent after plan selection');
          setShowSubscriptionPlans(false);
          setShowConsent(true);
        }}
      />
    );
  }

  // Show payment checkout page (after profile building, before final completion)
  // At this point the user exists in the system so subscription/payment APIs will work
  if (showCheckout && subscriptionSessionId) {
    return (
      <PaymentCheckoutPage
        sessionId={subscriptionSessionId}
        onComplete={() => {
          console.log('[Onboarding] Checkout complete, proceeding to completion page');
          setShowCheckout(false);
          setIsCompleted(true);
        }}
        onBack={() => {
          // Skip payment — go directly to completion (trial mode)
          console.log('[Onboarding] Skipping payment, proceeding to completion (trial)');
          setShowCheckout(false);
          setIsCompleted(true);
        }}
      />
    );
  }

  // Show separate Terms/Consent page (after plan selection, before building/checkout)
  if (showConsent && session?.sessionId) {
    return (
      <OnboardingConsentPage
        sessionId={session.sessionId}
        onComplete={(userEmail: string) => {
          let email = userEmail?.trim() || null;
          if (!email) {
            email = getStoredSessionEmail();
          }
          if (!email && typeof window !== 'undefined') {
            try {
              const raw = window.localStorage.getItem(LOCAL_USER_INFO_KEY);
              if (raw) {
                const parsed = JSON.parse(raw) as { signUpEmail?: string; email?: string };
                email = (parsed.signUpEmail ?? parsed.email ?? '').trim() || null;
              }
            } catch {
              // ignore
            }
          }
          if (email) {
            // Stay in onboarding flow: show profile step (building message + poll until profile created)
            setShowConsent(false);
            setShowAuth(false);
            setProfileStepEmail(email);
          } else {
            setShowConsent(false);
            setShowAuth(false);
            handleCompleteFlow(session.sessionId);
          }
        }}
      />
    );
  }

  // Show auth page after survey completes (or after plan selection to show consent)
  if (showAuth) {
    const sessionIdToShow = session?.sessionId || getStoredSessionId();
    console.log('[Onboarding] Showing AuthPage with:', { surveyCompleted, sessionId: sessionIdToShow });
    
    return (
      <AuthPage
        sessionId={sessionIdToShow || undefined}
        skipToConsent={false}
        fromOnboardingFlow={true}
        onAuthSuccess={() => {
          if (surveyCompleted) {
            // Auth successful after survey, show subscription plan selection page
            console.log('[Onboarding] Authentication successful, showing subscription plan selection');
            setShowAuth(false);
            setShowSubscriptionPlans(true);
          }
        }}
        onComplete={async () => {
          // Auth page no longer completes onboarding; terms/consent page does.
          return;
        }}
      />
    );
  }

  // Email gate: show ONLY when user came via "Get started" (top menu) with no email — they must enter email here.
  const showEmailGate =
    !isAuthLoading &&
    !isAuthContextAuthenticated &&
    !hasIntentEmail &&
    !showSubscriptionPlans &&
    !showCheckout &&
    !showConsent &&
    !isCompleted &&
    !profileStepEmail;

  if (showEmailGate) {
    // User landed via "Get started" (no email). Persist email and continue; start API will return REDIRECT_LOGIN or session.
    const handleEmailGateSubmit = () => {
      const email = emailGateValue.trim().toLowerCase();
      if (!email) {
        setEmailGateError('Please enter your email.');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setEmailGateError('Please enter a valid email address.');
        return;
      }
      setEmailGateError(null);
      persistIntentEmail(email);
      setIntentEmailFromGate(email);
      // createOrResumeSession will run (we now have intent email) and call start API; response handles REDIRECT_LOGIN or session
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
        <Header />
        <main className="flex flex-col items-center py-8 pt-20 sm:py-12 sm:pt-24">
          <div className="container-custom max-w-md w-full px-4 sm:px-6">
            <div className="rounded-2xl border border-border/50 bg-card/95 backdrop-blur-sm p-6 sm:p-8 shadow-lg animate-fade-in">
              <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary mb-1">
                Get started
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">
                Enter your email to continue
              </h1>
              <p className="text-sm text-muted-foreground mb-6">
                If you already have an account, we&apos;ll send you to sign in. Otherwise you&apos;ll start the onboarding survey.
              </p>
              <div className="space-y-3">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={emailGateValue}
                  onChange={(e) => setEmailGateValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleEmailGateSubmit()}
                  disabled={emailGateChecking}
                  className="w-full"
                  autoFocus
                />
                {emailGateError && (
                  <p className="text-sm text-destructive">{emailGateError}</p>
                )}
                <Button
                  onClick={handleEmailGateSubmit}
                  disabled={emailGateChecking}
                  className="w-full"
                >
                  {emailGateChecking ? (
                    <>
                      <BrandedSpinner className="mr-2 shrink-0" />
                      Checking…
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      <ProcessingOverlay
        isVisible={isProcessing}
        progress={processingProgress}
        currentStep={Math.floor(processingProgress / 25)}
        totalSteps={4}
      />
      <Header />
      <main className="flex flex-col items-center py-8 pt-20 sm:py-12 sm:pt-24">
        <div className="container-custom max-w-3xl w-full space-y-8 px-4 sm:px-6">
          {/* Header Card with Progress */}
          <div className="rounded-2xl border border-border/50 bg-card/95 backdrop-blur-sm p-6 sm:p-8 shadow-lg animate-fade-in">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-1">
                  <p className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-primary">
                    Meet Your Career Coach
                  </p>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                    {survey?.title || 'Guided Onboarding'}
                  </h1>
                  {survey?.description && (
                    <p className="text-sm text-muted-foreground max-w-lg">
                      {survey.description}
                    </p>
                  )}
                  {/* Debug: Show session ID during onboarding */}
                  {(session?.sessionId || getStoredSessionId()) && (
                    <div className="mt-2">
                      <CopyableId 
                        id={session?.sessionId || getStoredSessionId() || ''} 
                        label="Session ID"
                        showLabel={false}
                        className="text-xs"
                        codeClassName="text-xs"
                      />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Enhanced Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs font-medium">
                  <span className="text-muted-foreground">
                    Question {isCompleted ? visibleQuestions.length : Math.min(currentIndex + 1, visibleQuestions.length)} of {visibleQuestions.length}
                  </span>
                  <span className="text-primary font-semibold">{Math.round(progressValue)}%</span>
                </div>
                <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-primary via-primary to-primary/90 transition-all duration-500 ease-out shadow-sm"
                    style={{ width: `${progressValue}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-destructive-foreground animate-fade-in">
              <p className="font-semibold mb-1">Something went wrong</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="animate-fade-in-up">
            {renderContent()}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OnboardingPage;
